import { assign, createMachine, send, sendParent } from 'xstate';
import { choose } from 'xstate/lib/actions';
import AudacyLogger, { LoggerTopic } from '../../AudacyLogger';
import { PlayerAction, PlayerMetric, SaveResumePointTimings } from '../../Constants';
import Chapter from '../../dataServices/Chapter';
import Episode from '../../dataServices/Episode';
import { handleErrorAction, setQuickPauseChapterCache } from '../../utils';
import bufferingEventCounter from '../bufferingEventCounter';
import { type TStreamerMachineEvent, EContentType, type TPlayableObject } from '../types';
import {
  AUTO_TRANSITION,
  COMMON_ACTIONS,
  MAX_RETRY_ATTEMPTS,
  NON_REWINDABLE_ERROR,
  SCHEDULE_UPDATE_INTERVAL,
  SCHEDULE_UPDATE_JITTER,
} from './constants';
import {
  type ILiveStationAudioSource,
  type IStreamerMachineContext,
  type TStreamSelector,
} from './types';
import {
  DEFAULT_PLAYER_ACTIONS,
  episodeCoversTimestamp,
  getAvailableContentEndOffset,
  getChapterIndexFromOffset,
  getChapterSeekOffset,
  getChapterTimelineStart,
  getChaptersTotalDuration,
  getOffsetFromSeekTime,
  getExponentialBackoffDelay,
  getFirstAudioSourceUrl,
  getJitteredInterval,
  getLiveChapter,
  getLoadParams,
  getPlayableDataObject,
  isContentTypeAd,
  loadSourcesForItem,
  mapMp3PlayerTime,
  mergeMetadata,
  NO_CHAPTER_MATCH,
  normalizeResumePoint,
  saveResumePoint,
  saveResumePoints,
  type StreamType,
  toIntegerOrUndefined,
  reportPlaybackRequestedConviva,
  setContentInfoConviva,
  TLoadParams,
} from './utils';

// Test station slugs, e.g. /stations/wfan, on STG backend:
// Normal stations: wfan, 94wip, 1010wins, etc.
// Non-rewindable test case: test670thescore
// Alternate rewind/non-rewind each hour: test1010wins
// Also recommend setting useHls (below and in HtmlPlayer.ts) to false to test non-HLS on desktop Safari

const { RESUME_POINT_INTERVAL_MSEC } = SaveResumePointTimings;

console.log('[MP3RewindStreamer] ✅ Module loaded (v5 - OOP-738 rebased onto OOP-734/737/729)'); // OOP-869 TODO: remove these console logs — bump this marker when changing this file to detect stale Vite prebundle cache (rm -rf node_modules/.vite / vite --force)

// [OOP-734] Pull the loadAudio target-resolution logic out so it can be
// shared between the `choose` guard (does this chapter have a URL?) and the
// success-path assign (load and update ctx). Pure function — no side
// effects — so calling it twice in the same `choose` evaluation is safe.
const deriveLoadAudioTarget = (
  ctx: IMp3RewindStationMachineContext
): {
  chapterIdx: number;
  url: string | undefined;
  requestedOffset: number | undefined;
  usedLive: boolean;
  loadParams: TLoadParams | undefined;
} => {
  const { chapters, requestedOffset, playLive, isLiveMode, source } = ctx;

  const shouldUseLiveStream = (playLive || isLiveMode) && !!source;
  const chapterCount = chapters?.length ?? 0;
  // Range-validate the context index before trusting it: chapters can shrink
  // (show rollover / REPORT_METADATA correction) leaving a stale out-of-range
  // index behind. An out-of-range index is never a valid load target.
  let chapterIdx =
    ctx.currentChapterIndex !== undefined && ctx.currentChapterIndex < chapterCount
      ? ctx.currentChapterIndex
      : 0;
  let usedLive = false;
  let url;
  let loadParams;  

  if (shouldUseLiveStream) {
    try {
      loadParams = getLoadParams(ctx);
      url = loadParams.url;
      usedLive = true;
    } catch (e) {
      console.error('[MP3RewindStreamer] loadAudio: getLoadParams threw in live mode:', e); // OOP-869 TODO: remove these console logs
    }
    console.log(`[MP3RewindStreamer] loadAudio: live mode, url=${url}`); // OOP-869 TODO: remove these console logs
  } else {
    let useMp3Url = false;

    if (requestedOffset !== undefined && chapters && chapters.length > 0) {
      const lastChapter = chapters[chapters.length - 1];

      const duration = lastChapter.data.duration ?? 0;
      if (lastChapter.getStartOffset() + duration >= requestedOffset) {
        useMp3Url = true;
        // Resolve via the shared helper (single source of truth for
        // offset→index). No-match means the offset predates every chapter
        // (e.g. a stale offset carried in from an earlier episode) — load the
        // FIRST chapter deterministically instead of silently keeping
        // whatever index the context carried, which loaded the wrong
        // chapter's MP3 at position 0 with no error.
        const matched = getChapterIndexFromOffset(chapters, requestedOffset);
        chapterIdx = matched === NO_CHAPTER_MATCH ? 0 : matched;
      }
    }

    const chapter = chapters?.[chapterIdx];
    if (useMp3Url) {
      url = chapter?.getAudioUrl();
    } else {
      try {
        loadParams = getLoadParams(ctx);
        url = loadParams.url;
        usedLive = true;
      } catch (e) {
        console.error('[MP3RewindStreamer] loadAudio: getLoadParams threw in rewind mode:', e); // OOP-869 TODO: remove these console logs
      }
    }

    console.log(`[MP3RewindStreamer] loadAudio: chapter index=${chapterIdx}, url=${url ?? 'MISSING'} (requestedOffset=${requestedOffset})`); // OOP-869 TODO: remove these console logs
  }

  return { chapterIdx, url, requestedOffset, loadParams, usedLive };
};

// [OOP-765/OOP-731] Single source of truth for the continuePlaying
// stale-offset decision: `playlistNextStartTime` originates from player-side
// metadata and can be stale; it is only adoptable when it falls inside the
// current episode's window. Shared by the `isContinuePlayingStaleToLive`
// guard (analytics) and `setIsLiveAndRequestedOffset` (routing) so the two
// can never drift apart.
const isStaleContinueOffsetToLive = (
  ctx: IMp3RewindStationMachineContext,
  e: { playLive?: boolean; playlistNextStartTime?: number },
): boolean => {
  if (e.playLive) {
    return false;
  }
  const { episode } = ctx;
  const offset = e.playlistNextStartTime;
  const offsetWithinEpisode =
    !!episode &&
    offset !== undefined &&
    offset >= episode.getStartTimeSeconds() &&
    offset < episode.getEndTimeSeconds();
  return !offsetWithinEpisode;
};

// [OOP-734] About the REPORT_FAILURE + REPORT_PLAYBACK_FAILED pair spread
// into every playable state below: the player bridge (audioServices/utils.ts)
// emits TWO failure events depending on which native callback fired —
//   - REPORT_FAILURE         from ordinary player `error` events (transient
//                            HLS/MP3 load errors, decode errors, network
//                            blips) — utils.ts:85
//   - REPORT_PLAYBACK_FAILED from fatal `playbackFailed` events — utils.ts:96
// For MP3 Rewind both surface as "a chunk did not load" and the right
// recovery is the same configurable backoff retry path in FAILURE_WITH_RETRY.
// The handlers are duplicated inline (rather than spread from a single const)
// because xstate v4 typegen can't follow object spreads — without inline
// declarations, `sendNativePlaybackFailedEvent`'s event-type inference
// collapses to `never` and breaks tsc.
export type IMp3RewindStationMachineContext = {
  streamType: StreamType;
  playLive?: boolean;
  isLiveMode?: boolean;
  // [OOP-737] Sticky flag: true while playback is at the live edge of a still-
  // broadcasting show. Set when the wasPausedWhileLive auto-return branch
  // fires (PAUSED.PLAY) and on GO_TO_LIVE. Cleared on any explicit user seek that
  // signals "I want rewind content," not the live edge.
  //
  // The MP3 chunks served from the rewind backend at the live edge are
  // audibly the live broadcast but the player layer can't tag them with the
  // HLS `X-IS-LIVE` header — so metadata.isLive from REPORT_METADATA stays
  // false even when the listener is functionally at live. `assignMetadata`
  // reads this flag and forces metadata.isLive=true while it's set, giving
  // the UI a stable signal for the live indicator.
  playingLiveEdge?: boolean;
  // [OOP-729] End-of-show elapsed stashed by the advance triggers (which zero
  // elapsed to disarm the EOF guards); restored if the advance fails so the
  // scrubber stays at the end instead of snapping to 0 in PAUSED.
  elapsedBeforeAdvance?: number;
  seekingInChapter?: boolean;
  source?: ILiveStationAudioSource;
  url?: string;
  // [OOP-738] Seconds into the current chapter's MP3 file to start at, computed in
  // loadAudio and consumed by playWithOffset (the per-chapter analogue of HLS's
  // startTs). Undefined → play from current position (live / initial load).
  chapterSeekOffset?: number;
  // [OOP-734] Optional override for the maximum number of retry attempts on
  // playback failure. When undefined, WAITING_TO_RETRY's cond falls back to
  // the MAX_RETRY_ATTEMPTS constant. Lives on this subtype (not the base
  // IStreamerMachineContext) because only this machine's WAITING_TO_RETRY
  // reads it — putting it on the base would create a "looks supported but
  // isn't" trap for callers setting it on sibling streamers.
  maxRetryAttempts?: number;
  // [OOP-734] Counter for consecutive chapter-list fetch failures inside
  // PLAYING.UPDATING_CHAPTERS. Reset on PLAYING entry and on either
  // terminal exit from UPDATING_CHAPTERS (success or retry-exhausted
  // fallback). Distinct from `retryCount` (playback failure counter).
  chapterRetryCount?: number;
  // [OOP-756] Arms the out-of-range requestedOffset guard in getSchedule for
  // the FIRST schedule resolution after a LOADING entry only. Set by
  // `assignDefaults` (LOADING entry), cleared by getSchedule after one use.
  // Without this gate the guard would also run on every UPDATING_SCHEDULE
  // poll, where the listener's continuously-updated requestedOffset aging
  // out of the schedule window would flip an in-progress rewind session to
  // live-mode context (playLive/playingLiveEdge/episode swap) WITHOUT
  // stopping or reloading the player — the UI would show LIVE over rewind
  // audio and resume points would save against the wrong episode.
  validateOffsetOnLoad?: boolean;
} & IStreamerMachineContext<TPlayableObject>;

export const MP3_REWIND_MACHINE_ID = 'mp3RewindStationMachine';

export const hasFullMp3ChapterCoverage = (chapters: Chapter[]): boolean =>
  chapters.length > 0 && chapters.every((chapter) => chapter.getIsMP3());

// [OOP-738] Wrapper around the shared saveResumePoints. The server still gets
// the REAL-timeline resume point (episodeStart + elapsed — correct for
// cross-session resume), but ctx.requestedOffset must stay on the CHAPTER
// timeline (anchor = chapter[0].startOffset). The shared helper re-anchors it
// on the episode start;
const saveMp3ResumePoints = (
  ctx: IMp3RewindStationMachineContext,
  shouldSaveOnServer: boolean,
): IMp3RewindStationMachineContext => {
  if (ctx.isLiveMode) {
    return ctx;
  }
  const saved = saveResumePoints(ctx, shouldSaveOnServer) as IMp3RewindStationMachineContext;
  const { chapters, elapsed, currentChapterIndex } = ctx;
  // requestedOffset unchanged = the shared helper's early return (no entity /
  // no elapsed) or its keep-old-offset-during-ads case — nothing to re-anchor.
  if (
    !chapters?.length ||
    elapsed === undefined ||
    currentChapterIndex === undefined ||
    // Range check, not just undefined: chapters can shrink (show rollover /
    // REPORT_METADATA correction) while a stale higher index survives in ctx.
    // Without this, `chapters[currentChapterIndex]` below is undefined and the
    // .getStartOffset() call throws, killing the resume-save path.
    currentChapterIndex >= chapters.length ||
    saved.requestedOffset === ctx.requestedOffset
  ) {
    return saved;
  }

  const intraChapterOffset = elapsed - getChapterTimelineStart(chapters, currentChapterIndex);
  const reanchored = chapters[currentChapterIndex].getStartOffset() + Math.max(0, intraChapterOffset);
  return { ...saved, requestedOffset: reanchored };
};

// Single source of truth for "what is the current chapter index" during MP3
// playback, shared by assignCurrentChapterIndex / assignCurrentChapterIndexWithOffset.
// Live pins to the synthetic tail; otherwise offset→index via the shared
// helper. No-match (stale offset predating every chapter) and out-of-range
// context indexes both resolve to a VALID index for the CURRENT chapters
// array — a stale index is never carried forward (chapters can shrink on
// show rollover / REPORT_METADATA correction; see saveMp3ResumePoints).
const resolveChapterIndex = (ctx: IMp3RewindStationMachineContext): number => {
  const { chapters, requestedOffset, currentChapterIndex, metadata } = ctx;
  const chapterCount = chapters?.length ?? 0;
  if (metadata?.isLive && chapterCount > 0) {
    return chapterCount - 1;
  }
  const matched = getChapterIndexFromOffset(chapters, requestedOffset);
  if (matched !== NO_CHAPTER_MATCH) {
    return matched;
  }
  // No offset / offset before every chapter: keep the current index only if
  // it's still valid for this chapters array, else anchor on the first chapter.
  return currentChapterIndex !== undefined && currentChapterIndex < chapterCount
    ? currentChapterIndex
    : 0;
};

// [OOP-729] Index of the last REAL chapter, derived from the ARRAY, not the
// wall clock. The synthetic live sentinel (getLiveChapter: duration 0) is
// appended at fetch time gated on isLiveOnAir() — but guards evaluate at
// EVENT time. A show that ends while the listener is on its last real
// chapter leaves the sentinel in ctx.chapters until the next successful
// refresh (jittered poll; unbounded inside CHAPTERS_REFRESH_FAILED), so a
// wall-clock isLiveOnAir() check would misidentify the sentinel as a real
// "next chapter" and advance into a URL-less synthetic → playback failure.
const lastRealChapterIdx = (
  chapters: IMp3RewindStationMachineContext['chapters'],
): number => {
  if (!chapters || chapters.length === 0) {
    return -1;
  }
  const last = chapters[chapters.length - 1];
  return last?.getDuration() === 0 ? chapters.length - 2 : chapters.length - 1;
};

// `elapsed` is the show-relative cumulative position (see mapMp3PlayerTime), so
// the end of playback is the sum of all chapter durations — the same continuous
// timeline the scrub bar spans — not the single entity's reported duration, which
// for fixtures doesn't match the summed chapter lengths. [OOP-740]
const isPlayingAfterShowEnd = (ctx: IMp3RewindStationMachineContext): boolean => {
  const { chapters, elapsed, episode, standaloneChapter } = ctx;
  const entity = standaloneChapter ?? episode;

  if (ctx.isLiveMode) {
    const startTime = entity?.getStartTimeSeconds() ?? 0;
    const endTime = entity?.getEndTimeSeconds() ?? 0;
    return startTime + (elapsed ?? 0) >= endTime;
  }

  const showDuration = getChaptersTotalDuration(chapters) || entity?.getDuration() || 0;
  return (elapsed ?? 0) > showDuration;
};

type TMp3RewindStationMachineServices = {
  getSchedule: {
    data: Awaited<IMp3RewindStationMachineContext>;
  };
  getChapters: {
    data: Awaited<IMp3RewindStationMachineContext>;
  };
  loadStationSources: {
    data: Awaited<IMp3RewindStationMachineContext>;
  };
  // [OOP-729] Resolves the next chronological show in the station's schedule.
  // The service returns the updated context with `ctx.episode` set to the next
  // show and `ctx.requestedOffset` cleared, so the standard LOADING flow picks
  // up the new episode and begins playback from its first chapter.
  advanceToNextShow: {
    data: Awaited<IMp3RewindStationMachineContext>;
  };
};

const mp3RewindStationMachine = createMachine(
  {
    predictableActionArguments: true,
    id: MP3_REWIND_MACHINE_ID,
    initial: 'LOADING',
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import("./MP3RewindStationMachine.typegen").Typegen0,
    schema: {
      context: {
        timeSinceLastSavedResumePoint: Date.now(),
      } as IMp3RewindStationMachineContext,
      events: {} as TStreamerMachineEvent,
      services: {} as TMp3RewindStationMachineServices,
    },
    states: {
      LOADING: {
        id: 'loading',
        entry: 'assignDefaults',
        initial: 'LOADING_SOURCES',
        states: {
          LOADING_SOURCES: {
            invoke: {
              id: 'loadStationSources',
              src: 'loadStationSources',
              onDone: {
                actions: ['assignRewindContext'],
                target: 'LOADING_SCHEDULE',
              },
              onError: {
                actions: [
                  'sendPlaybackFailedToLoadEvent',
                  handleErrorAction('LOADING_SOURCES', 'rewindStationMachine'),
                ],
                target: 'FAILED_TO_LOAD',
              },
            },
          },
          LOADING_SCHEDULE: {
            invoke: {
              id: 'getSchedule',
              src: 'getSchedule',
              onDone: {
                actions: [
                  'assignScheduleResponse',
                  'assignMetadata',
                  'assignCurrentChapterIndexWithOffset',
                  'loadAudio',
                  'clearPlayLive',
                ],
              },
              onError: {
                actions: [
                  'sendPlaybackFailedToLoadEvent',
                  handleErrorAction('LOADING_SCHEDULE', 'rewindStationMachine'),
                ],
                target: 'FAILED_TO_LOAD',
              },
            },
          },
          FAILED_TO_LOAD: {
            entry: ['logLoadingError', 'assignTrackError'],
            after: [
              {
                delay: AUTO_TRANSITION,
                target: '#failureWithRetry',
              },
            ],
          },
        },
        on: {
          REPORT_LOADED: {
            target: 'LOADED',
          },
          REPORT_METADATA: {
            actions: 'assignMetadata',
          },
          PAUSE: {
            target: 'PAUSED',
            actions: ['stop'],
          },
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      LOADED: {
        entry: 'autoplay',
        on: {
          PLAY: {
            target: 'PLAY_REQUESTED',
          },
          REPORT_METADATA: {
            actions: 'assignMetadata',
          },
          // Destroy stream when autoplay is false to stop infinite HLS buffering
          // Go straight to PAUSED because the audio was never loaded in the first place on mobile
          PAUSE: {
            target: 'PAUSED',
            actions: ['stop'],
          },
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      PLAY_REQUESTED: {
        id: 'playRequested',
        entry: 'playWithOffset',
        on: {
          REPORT_PLAYING: {
            actions: ['sendPlayEvent', 'addToHistory'],
            target: 'PLAYING',
          },
          REPORT_METADATA: {
            actions: 'assignMetadata',
          },
          REPORT_AUTOPLAY_POLICY: {
            target: 'PAUSED',
          },
          PAUSE: {
            target: 'PAUSED',
            actions: ['stop'],
          },
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      TRANSITION_TO_BUFFERING: {
        entry: ['sendBufferingEvent'],
        on: {
          REPORT_PLAYING: {
            actions: ['sendResumeFromBufferingEvent'],
            target: 'PLAYING',
          },
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
        after: [
          {
            // When android buffers data, it will enter a loading state and a pause state directly after.
            // This will cause the player to pause the stream. This will ignore that pause event then let the user pause manually
            target: 'BUFFERING',
            delay: 100,
          },
        ],
      },
      BUFFERING: {
        on: {
          PAUSE: {
            target: 'PAUSE_REQUESTED',
          },
          REPORT_LOADING: {
            target: 'TRANSITION_TO_BUFFERING',
          },
          REPORT_PAUSED: {
            target: 'PAUSED',
          },
          REPORT_PLAYING: {
            actions: ['sendResumeFromBufferingEvent'],
            target: 'PLAYING',
          },
          REPORT_METADATA: {
            actions: ['sendLiveSongListenedEvent', 'assignMetadata'],
          },
          STOP: {
            target: 'PAUSED',
            actions: ['stop', 'sendStopEvent'],
          },
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      PLAYING: {
        initial: 'IS_PLAYING',
        // [OOP-734] Reset both retry counters on (re-)entry to PLAYING so any
        // in-flight retry budget is cleared when the user transitions away
        // and comes back. Without resetting chapterRetryCount here, a
        // half-spent budget would survive PAUSE → RESUME (PLAYING is exited
        // for PAUSED and re-entered on play), leaving the next failure burst
        // with fewer retries than the user would expect.
        entry: ['assignRetryCountToZero', 'resetChapterRetryCount'],
        // [OOP-741] Content poller. Owns its own wall-clock interval and fires
        // REFRESH_CHAPTERS on a jittered cadence, replacing the `after`
        // delayed transitions that previously drove polling. Those timers were
        // re-armed from zero on every exit/re-entry of IS_PLAYING — and the
        // ~1/sec REPORT_TIME_UPDATE / REPORT_METADATA churn plus buffering
        // blips re-entered it often enough that the 45–75s delay rarely
        // elapsed. Invoking on the PLAYING compound node keeps the interval
        // alive across all child transitions (IS_PLAYING ↔ UPDATING_CHAPTERS ↔
        // UPDATING_SCHEDULE ↔ CHAPTERS_REFRESH_FAILED), so the poll cadence is
        // decoupled from the transition lifecycle. Torn down and restarted on
        // a full buffering excursion (which exits PLAYING) — acceptable, since
        // that restarts the interval rather than silently starving a one-shot.
        invoke: {
          id: 'pollContent',
          src: 'pollContent',
        },
        states: {
          IS_PLAYING: {
            on: {
              // Targetless: routing to UPDATING_CHAPTERS on every metadata
              // event refetched chapters at player-metadata cadence (every few
              // seconds) and reset the `after` poll timer on each round trip.
              // Content refresh belongs to the jittered UPDATING_SCHEDULE poll.
              REPORT_METADATA: {
                actions: ['assignMetadata', 'clearPlayLive'],
              },
              // [OOP-741] The `pollContent` service (invoked on PLAYING) fires
              // this on a jittered interval to refresh the chapter list and
              // recompute `availableContentEndOffset`, so the scrubber's
              // MP3-available region grows as new content is published.
              REFRESH_CHAPTERS: {
                target: 'UPDATING_CHAPTERS',
              },
              REPORT_TIME_UPDATE: [
                {
                  cond: 'isFinishedStandaloneChapter',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed', 'sendReportEnded'],
                },
                // [OOP-730] Reached the end of the last available MP3 chapter
                // (caught up with live). Save the point, then jump to the live
                // edge — same action triple as `GO_TO_LIVE` / the
                // `wasPausedWhileLive` branch in PAUSED.PLAY: clear the rewind
                // episode and set playLive so the next LOADING resolves to
                // the live stream. Must be evaluated BEFORE
                // `isPlayingAfterEntityEnd`, which would otherwise drop us
                // into UPDATING_SCHEDULE and miss the live transition. The
                // sibling OOP-729 branch below is mutually exclusive (live
                // vs. past show), so their relative order doesn't matter as
                // long as both precede the entity-end fallback.
                {
                  cond: 'isFinishedLastAvailableChapter',
                  // [OOP-731] `sendAutoJumpToLiveEvent` fires the analytics
                  // event for this auto-trigger path. Ordering matters here:
                  //
                  // - MUST run AFTER `assignTime` so `ctx.elapsed` reflects
                  //   the just-reported player time (the listener's actual
                  //   position) — not the previous tick's stale value.
                  // - MUST run BEFORE `resetElapsed` which zeroes
                  //   `ctx.elapsed`. Otherwise the payload's
                  //   `currentPosition` would be `episode.getStartTimeSeconds()
                  //   + 0` instead of the real position.
                  // - MUST run BEFORE `exitRewindMode` which clears
                  //   `ctx.episode`, since the payload's `contentId` is
                  //   resolved from `episode.data.id`.
                  //
                  // `logLiveTransition` also reads `ctx.elapsed`, so it shares
                  // the "MUST run BEFORE `resetElapsed`" constraint — otherwise
                  // the info log would always report `elapsed: 0`.
                  //
                  // `saveResumePoint` is the inferred boundary: it depends on
                  // current elapsed (so must come after assignTime) but
                  // doesn't mutate it (so order vs analytics is just
                  // readability).
                  actions: [
                    'assignTime',
                    'saveResumePoint',
                    'sendAutoJumpToLiveEvent',
                    'logLiveTransition',
                    'resetElapsed',
                    'stop',
                    'exitRewindMode',
                    'setPlayLive',
                    'setPlayingLiveEdge',
                  ],
                  target: '#loading',
                },
                // Live rollover: at the live edge the current show ended, so
                // the broadcast moved to the next show. Refresh the schedule
                // (re-resolve the on-air episode + chapters) while the live
                // stream keeps playing — no stop/reload. Must precede
                // isFinishedCompletePastShow (the stop+advance past-show path).
                {
                  cond: 'isLiveShowRolledOver',
                  actions: ['assignTime', 'clearEpisodeForRollover'],
                  target: 'UPDATING_SCHEDULE',
                },
                // [OOP-729] Finished the last chapter of a complete (non-live)
                // show. Save the resume point, stop the player, then enter
                // ADVANCING_TO_NEXT_SHOW which fetches the next chronological
                // episode and resets context so the standard LOADING flow
                // picks it up. Must be evaluated BEFORE isPlayingAfterEntityEnd
                // (the broader entity-end branch) — that one routes to
                // UPDATING_SCHEDULE and would otherwise pre-empt this. Mutually
                // exclusive with OOP-730's branch above (live vs. past show).
                {
                  cond: 'isFinishedCompletePastShow',
                  // `sendStopEvent` fires the listening-analytics end-of-show
                  // signal for the show we're leaving behind; without it the
                  // previous show's session has no observable end event
                  // (parallels the standalone-chapter end-of-content path).
                  actions: [
                    'assignTime',
                    'saveResumePoint',
                    'stashElapsedForAdvance',
                    'stop',
                    'sendStopEvent',
                  ],
                  target: 'ADVANCING_TO_NEXT_SHOW',
                },
                {
                  cond: 'isPlayingAfterEntityEnd',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed'],
                  target: 'UPDATING_SCHEDULE',
                },

                {
                  // Targetless: a `target: 'IS_PLAYING'` self-transition here
                  // is external — it re-enters the state every second and
                  // resets the `after` poll timer, so the poll never fires.
                  actions: ['assignTime', 'assignCurrentChapterIndex', 'saveLocalResumePoint'],
                },
              ],
              REPORT_FAILURE: {
                target: '#failureWithRetry',
                actions: 'sendNativePlaybackFailedEvent',
              },
              REPORT_PLAYBACK_FAILED: {
                target: '#failureWithRetry',
                actions: 'sendNativePlaybackFailedEvent',
              },
            },
          },
          UPDATING_SCHEDULE: {
            invoke: {
              id: 'getSchedule',
              src: 'getSchedule',
              onDone: [
                {
                  cond: 'isNonRewindableContent',
                  actions: [
                    'stop',
                    'exitRewindMode',
                    'setPlayLive',
                    'setPlayingLiveEdge',
                    handleErrorAction('UPDATING_SCHEDULE', 'rewindStationMachine'),
                  ],
                  target: '#loading',
                },
                {
                  actions: [
                    'assignScheduleResponse',
                    'assignMetadata',
                    'addEpisodeToHistory',
                    'assignCurrentChapterIndex',
                  ],
                  target: 'IS_PLAYING',
                },
              ],
              onError: [
                {
                  cond: 'isNonRewindableContentError',
                  actions: [
                    'stop',
                    'exitRewindMode',
                    'setPlayLive',
                    'setPlayingLiveEdge',
                    handleErrorAction('UPDATING_SCHEDULE', 'rewindStationMachine'),
                  ],
                  target: '#loading',
                },
                {
                  // [OOP-734] Poll failures must not affect the current
                  // playback session (per AC). Previously this branch had no
                  // `target`, which left the machine stuck in
                  // UPDATING_SCHEDULE until some other event fired it out.
                  // Returning to IS_PLAYING resumes normal playback; a fresh
                  // schedule poll happens the next time playback crosses the
                  // entity end (`isPlayingAfterEntityEnd`). [OOP-741] The old
                  // periodic `after: SCHEDULE_UPDATE_INTERVAL` re-arm no longer
                  // applies — that timer was removed when the `pollContent`
                  // service took over polling (it drives chapter refresh, not
                  // schedule).
                  //
                  // [OOP-731] `sendSchedulePollFailedEvent` fires the
                  // observability signal so the analytics pipeline can
                  // detect transient backend issues without correlating
                  // through log ingestion. Low-severity — audio continues.
                  actions: [
                    'sendSchedulePollFailedEvent',
                    handleErrorAction('UPDATING_SCHEDULE', 'rewindStationMachine'),
                  ],
                  target: 'IS_PLAYING',
                },
              ],
            },
          },
          UPDATING_CHAPTERS: {
            invoke: {
              id: 'getChapters',
              src: 'getChapters',
              onDone: {
                actions: [
                  'assignChapterResponse',
                  'assignCurrentChapterIndex',
                  // [OOP-734] Reset the chapter-retry counter on success so a
                  // future independent failure gets a fresh retry budget.
                  'resetChapterRetryCount',
                ],
                target: 'IS_PLAYING',
              },
              onError: [
                {
                  // [OOP-734] First failure: retry once before falling back.
                  // Re-entering UPDATING_CHAPTERS via external transition
                  // re-invokes the service. Caps at one retry — see
                  // `canRetryChapterFetch` guard. Addresses the AC item "On
                  // chapter transition failure, the player should not silently
                  // stall" — instead of the previous log+continue (which is
                  // effectively a silent failure for the chapter list), we
                  // give transient errors a chance to self-heal.
                  cond: 'canRetryChapterFetch',
                  actions: [
                    'incrementChapterRetryCount',
                    handleErrorAction('UPDATING_CHAPTERS', 'rewindStationMachine'),
                  ],
                  target: 'UPDATING_CHAPTERS',
                },
                {
                  // [OOP-734] Exhausted retry: enter a distinct
                  // `CHAPTERS_REFRESH_FAILED` state instead of silently
                  // returning to IS_PLAYING. The UI can match on this state
                  // to surface a "chapter list could not refresh — retry"
                  // affordance to the listener (audio continues playing
                  // either way). Resets the counter so the next refresh
                  // attempt isn't pre-burned.
                  actions: [
                    'resetChapterRetryCount',
                    'sendChaptersRefreshFailedEvent',
                    handleErrorAction('UPDATING_CHAPTERS', 'rewindStationMachine'),
                  ],
                  target: 'CHAPTERS_REFRESH_FAILED',
                },
              ],
            },
          },
          CHAPTERS_REFRESH_FAILED: {
            // [OOP-734] Distinct user-facing error state for exhausted
            // chapter-list refresh attempts. Audio continues playing — the
            // chapter list itself is stale, not the audio — but the UI now
            // has a stable signal to surface a retry affordance.
            //
            // Recovery paths:
            //   - `REFRESH_CHAPTERS` event: both the manual retry from the UI
            //     and the [OOP-741] `pollContent` interval fire this, so the
            //     poller doubles as the auto-retry on the polling cadence
            //     (previously a dedicated `after` timer here).
            //   - `REPORT_METADATA` (natural recovery: next metadata
            //     refresh triggers a fresh chapter fetch)
            on: {
              REFRESH_CHAPTERS: {
                target: 'UPDATING_CHAPTERS',
              },
              REPORT_METADATA: {
                actions: ['assignMetadata', 'clearPlayLive'],
                target: 'UPDATING_CHAPTERS',
              },
              REPORT_TIME_UPDATE: [
                {
                  cond: 'isFinishedStandaloneChapter',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed', 'sendReportEnded'],
                },
                // [OOP-729] Mirror IS_PLAYING's end-of-content branches: audio
                // keeps playing in this state (only the chapter LIST is stale),
                // so the OOP-730 catch-up-to-live jump and the OOP-729
                // past-show auto-advance must still fire here — otherwise the
                // broader isPlayingAfterEntityEnd branch below routes to
                // UPDATING_SCHEDULE and both transitions are missed.
                {
                  cond: 'isFinishedLastAvailableChapter',
                  // Full mirror of IS_PLAYING's branch incl. the OOP-731
                  // analytics pair (before resetElapsed/exitRewindMode — see
                  // the ordering comment there).
                  actions: [
                    'assignTime',
                    'saveResumePoint',
                    'sendAutoJumpToLiveEvent',
                    'logLiveTransition',
                    'resetElapsed',
                    'stop',
                    'exitRewindMode',
                    'setPlayLive',
                    'setPlayingLiveEdge',
                  ],
                  target: '#loading',
                },
                {
                  cond: 'isLiveShowRolledOver',
                  actions: ['assignTime', 'clearEpisodeForRollover'],
                  target: 'UPDATING_SCHEDULE',
                },
                {
                  cond: 'isFinishedCompletePastShow',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed', 'stop', 'sendStopEvent'],
                  target: '#advancingToNextShow',
                },
                {
                  cond: 'isPlayingAfterEntityEnd',
                  actions: ['assignTime', 'saveResumePoint', 'resetElapsed'],
                  target: 'UPDATING_SCHEDULE',
                },
                {
                  actions: ['assignTime', 'assignCurrentChapterIndex', 'saveLocalResumePoint'],
                },
              ],
              REPORT_FAILURE: {
                target: '#failureWithRetry',
                actions: 'sendNativePlaybackFailedEvent',
              },
              REPORT_PLAYBACK_FAILED: {
                target: '#failureWithRetry',
                actions: 'sendNativePlaybackFailedEvent',
              },
            },
          },
          // [OOP-729] Auto-advance to the next chronological show. The service
          // resolves the next episode in the station's schedule, assigns it to
          // ctx.episode, and clears chapter/offset state. On done we re-enter
          // the top-level LOADING state, which runs the standard
          // LOADING_SOURCES → LOADING_SCHEDULE → LOADED → PLAY pipeline against
          // the newly-set episode, including autoplay (so the next show starts
          // playing without user interaction).
          //
          // On error (no next show in schedule, network failure, etc.) we
          // fall back to PAUSED via `#paused` — preferable to silently
          // looping or dropping the user into an undefined state.
          // `sendStopEvent` is NOT in this onError actions list because
          // PAUSED's own entry already fires it (`PAUSED.entry:
          // ['sendStopEvent', 'assignRetryCountToZero']`). Including it
          // here would double-fire analytics for the same transition.
          ADVANCING_TO_NEXT_SHOW: {
            id: 'advancingToNextShow',
            invoke: {
              id: 'advanceToNextShow',
              src: 'advanceToNextShow',
              // [OOP-731] `sendAutoAdvanceShowEvent` MUST run AFTER
              // `assignAdvancedShow` — the analytics payload's `contentId`
              // reads from `ctx.episode.data.id`, and `assignAdvancedShow` is
              // what swaps the OLD episode out for the NEW one. Running it
              // BEFORE would fire the event with the outgoing show's id,
              // conflating the pair (old-show stop → new-show start) with the
              // "started new show" signal.
              onDone: {
                actions: ['assignAdvancedShow', 'sendAutoAdvanceShowEvent'],
                target: '#loading',
              },
              onError: {
                // [OOP-731] `sendAutoAdvanceFailedEvent` makes the fallback
                // observable — PAUSED.entry's sendStopEvent alone is
                // indistinguishable from a user pause.
                actions: [
                  'sendAutoAdvanceFailedEvent',
                  'restoreElapsedAfterFailedAdvance',
                  handleErrorAction('ADVANCING_TO_NEXT_SHOW', 'rewindStationMachine'),
                ],
                target: '#paused',
              },
            },
          },
        },
        on: {
          GO_TO_LIVE: {
            actions: ['stop', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
            target: 'LOADING',
          },
          SEEK_TIME: [
            {
              cond: 'hasSeekedToEndOfStandaloneChapter',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            // [OOP-738] Seek landed on the synthetic live chapter → hand off to
            // live. Reuses OOP-730's exitRewindMode/setPlayLive without changing it.
            {
              cond: 'isSeekToLiveChapter',
              // [OOP-731] analytics BEFORE exitRewindMode (episode → contentId).
              actions: ['stop', 'sendMp3SeekToLiveEvent', 'clearPlayingLiveEdge', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            // [OOP-738] Seek stays within the chapter whose MP3 is already loaded →
            // seek inside the file instead of reloading (mirrors podcast seekTime).
            {
              cond: 'isSeekInCurrentChapter',
              actions: ['assignSeekTime', 'seekWithinChapter', 'saveResumePoint', 'clearPlayingLiveEdge'],
            },
            // [OOP-738] Seek lands in a different chapter → reload that chapter's
            // MP3 and start it at the intra-chapter offset (loadAudio +
            // playWithOffset).
            {
              cond: 'hasSeekedPastLive',
              // [OOP-731] analytics BEFORE exitRewindMode (episode → contentId).
              actions: ['stop', 'sendMp3SeekToLiveEvent', 'resetElapsed', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            {
              // [OOP-737] An explicit user seek (including chapter taps, which fire
              // SEEK_TIME with the chapter's start time) signals intent to stay in
              // rewind. assignSeekTime sets isLiveMode:false, so subsequent
              // PAUSE/RESUME restores
              // the rewind offset instead of jumping back to live.
              actions: ['stop', 'assignSeekTime', 'assignCurrentChapterIndexWithOffset', 'saveResumePoint', 'clearPlayingLiveEdge'],
              target: 'LOADING',
            },
          ],
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_LOADING: {
            target: 'TRANSITION_TO_BUFFERING',
          },
          REPORT_ENDED: [
            {
              cond: 'isStandaloneChapter',
              actions: ['stop', 'handleRewindContentEnded', 'sendStopEvent'],
              target: 'PAUSED',
            },
            // [OOP-738] A non-last chapter's MP3 ended → auto-advance to the next
            // chapter and load its file. The LAST real chapter ending falls through
            // to the go-live branch below; OOP-730 handles catch-up-to-live mid-play.
            {
              cond: 'hasNextChapter',
              // [OOP-731] Payload reads only episode + elapsed (the OLD
              // chapter's end position — the last moment of played content),
              // which advanceToNextChapter doesn't touch; ordering after it is
              // for readability, not correctness.
              actions: ['stop', 'advanceToNextChapter', 'sendAutoAdvanceChapterEvent'],
              target: 'LOADING',
            },
            // [OOP-729] Last chapter of a completed past broadcast → auto-
            // advance to the next chronological show. Must precede the
            // isPlayingRewindContent bounce-to-live branch, otherwise a past
            // show that ends jumps to live instead of the next past show.
            {
              cond: 'isCompletePastBroadcast',
              // resetElapsed AFTER saveResumePoint (which reads elapsed):
              // zeroing it disarms the EOF guards so a straggler post-stop
              // player event can't re-match and externally re-enter
              // #advancingToNextShow (which would restart the invoke and
              // double-fire sendStopEvent).
              actions: ['saveResumePoint', 'stashElapsedForAdvance', 'stop', 'sendStopEvent'],
              target: '#advancingToNextShow',
            },
            {
              cond: 'isPlayingRewindContent',
              // [OOP-730/OOP-731] On a live show this branch IS the primary
              // auto-jump-to-live trigger (clean file EOF on the last real
              // chapter falls through hasNextChapter/isCompletePastBroadcast
              // to here). sendAutoJumpToLiveEvent MUST run BEFORE
              // handleRewindContentEnded, which clears ctx.episode (the
              // payload's contentId source). setPlayingLiveEdge keeps the
              // live-indicator signal consistent with the REPORT_PAUSED and
              // GO_TO_LIVE live-transition paths.
              actions: [
                'stop',
                'sendAutoJumpToLiveEvent',
                'handleRewindContentEnded',
                'setPlayingLiveEdge',
                'sendStopEvent',
              ],
              target: 'LOADING',
            },
          ],
          REPORT_CONTINUE_PLAYING: [
            {
              cond: 'shouldStandaloneChapterStop',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            // [OOP-765] `continuePlaying` fires when the player still carries a
            // playlistNextStartTime (e.g. stale HLS metadata). setIsLiveAndRequestedOffset
            // adopts that offset only when it falls inside the current episode
            // window, otherwise routes to live — which supersedes OOP-738's
            // earlier continue-playing routing. (Chapter auto-advance on a clean
            // end is handled by REPORT_ENDED / REPORT_PAUSED, not here.)
            // [OOP-731] Stale playlistNextStartTime → auto-route to live: fire
            // the auto-live analytics (episode still in ctx — the action keeps
            // it) so this OOP-765 transition is observable. Guard mirrors the
            // action's internal decision; the action still owns the routing.
            {
              cond: 'isContinuePlayingStaleToLive',
              // setPlayingLiveEdge keeps the LIVE indicator consistent with
              // every other live-arrival path (without it the UI showed the
              // "back to live" button at the live edge).
              actions: ['sendAutoJumpToLiveEvent', 'setIsLiveAndRequestedOffset', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            {
              cond: 'isNotLiveMode',
              actions: ['setIsLiveAndRequestedOffset'],
              target: 'LOADING',
            },
          ],
          REPORT_PAUSED: [
            {
              cond: 'isStandaloneChapter',
              actions: ['handleRewindContentEnded', 'sendStopEvent'],
              target: 'PAUSED',
            },
            // [OOP-738] A pause reported at the natural end of a non-last chapter is
            // really "chapter finished" → auto-advance to the next chapter instead
            // of stopping. Real user pauses (position not at the chapter end) fall
            // through to PAUSED below.
            {
              cond: 'isFinishedNonLastChapter',
              // [OOP-731] Same payload note as the REPORT_ENDED wire above.
              actions: ['stop', 'advanceToNextChapter', 'sendAutoAdvanceChapterEvent'],
              target: '#loading',
            },
            // [OOP-738] Same native quirk for the LAST real chapter on a live
            // show: its natural end can arrive as a PAUSE instead of
            // REPORT_ENDED, before OOP-730's time-update guard has a chance to
            // fire. That end means "caught up to live", so hand off with the
            // same action triple — otherwise the player sits in PAUSED at the
            // end of the show.
            {
              cond: 'isFinishedLastRealChapter',
              // [OOP-731] Same semantic as OOP-730's catch-up-to-live, just
              // triggered via the PAUSE-at-EOF native quirk — reuse
              // sendAutoJumpToLiveEvent (BEFORE exitRewindMode).
              actions: ['stop', 'sendAutoJumpToLiveEvent', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: '#loading',
            },
            // [OOP-729] Past-show counterpart: PAUSE-at-EOF on the LAST chapter
            // of a completed show means the show is over → auto-advance to the
            // next chronological show (mirrors the REPORT_ENDED
            // isCompletePastBroadcast branch).
            {
              cond: 'isFinishedLastChapterOfPastShow',
              actions: ['saveResumePoint', 'stashElapsedForAdvance', 'stop', 'sendStopEvent'],
              target: '#advancingToNextShow',
            },
            {
              target: 'PAUSED',
            },
          ],
          PAUSE: {
            target: 'PAUSE_REQUESTED',
          },
          STOP: {
            target: 'PAUSE_REQUESTED',
          },
          REPORT_TIME_UPDATE: [
            {
              cond: 'isPlayingAfterEntityEnd',
              actions: 'sendReportEnded',
            },
            {
              actions: 'assignTime',
            },
          ],
          REPORT_METADATA: {
            actions: ['assignMetadata', 'clearPlayLive'],
          },
          ...COMMON_ACTIONS,
        },
      },
      PAUSE_REQUESTED: {
        entry: choose([
          {
            cond: 'isAd',
            actions: 'pause',
          },
          {
            cond: 'isRewindable',
            actions: ['saveResumePoint', 'cacheChaptersOnPause', 'stop'],
          },
          {
            actions: 'stop',
          },
        ]),
        on: {
          ...COMMON_ACTIONS,
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PAUSED: {
            target: 'PAUSED',
          },
        },
      },
      PAUSED: {
        // [OOP-729] id needed so the ADVANCING_TO_NEXT_SHOW onError branch can
        // target this state via '#paused' from a nested child state.
        id: 'paused',
        entry: ['sendStopEvent', 'assignRetryCountToZero'],
        on: {
          GO_TO_LIVE: {
            actions: ['stop', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
            target: 'LOADING',
          },
          SEEK_TIME: [
            {
              cond: 'hasSeekedToEndOfStandaloneChapter',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            // [OOP-738] Seek onto the synthetic live chapter → hand off to live.
            {
              cond: 'isSeekToLiveChapter',
              // [OOP-731] analytics BEFORE exitRewindMode (episode → contentId).
              actions: ['stop', 'sendMp3SeekToLiveEvent', 'clearPlayingLiveEdge', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            // No in-chapter fast path while paused: the MP3 is stopped/unloaded, so
            // every seek reloads the target chapter and seeks via playWithOffset.
            {
              cond: 'hasSeekedPastLive',
              // [OOP-731] analytics BEFORE exitRewindMode (episode → contentId).
              actions: ['stop', 'sendMp3SeekToLiveEvent', 'resetElapsed', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            {
              // [OOP-737] An explicit user seek (including chapter taps, which fire
              // SEEK_TIME with the chapter's start time) signals intent to stay in
              // rewind. assignSeekTime sets isLiveMode:false, so subsequent
              // PAUSE/RESUME restores
              // the rewind offset instead of jumping back to live.
              actions: ['stop', 'assignSeekTime', 'assignCurrentChapterIndexWithOffset', 'saveResumePoint', 'clearPlayingLiveEdge'],
              target: 'LOADING',
            },
          ],
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYING: {
            target: 'PLAYING',
          },
          PLAY: [
            // [OOP-737] Product ruling (2026-07-28): the resume decision keys
            // on WHAT WAS PLAYING AT PAUSE TIME, not on where the session
            // originated. Paused while on the LIVE stream → resume jumps to
            // the current live point (never the stale buffered position).
            // Paused while playing REWIND content — chapters AND their baked-
            // in ads — → resume exactly in place (the default branch below).
            // This check comes BEFORE isAd: a pause during a live-stream ad
            // is still "paused while live streaming" (the broadcast moved on;
            // resuming a stale live ad would put the listener behind live).
            //
            // [OOP-731] `sendLiveOriginResumeEvent` fires the analytics event
            // for this auto-return path. MUST run BEFORE `exitRewindMode`
            // (which clears `ctx.episode`, breaking the payload's `contentId`
            // resolution) — same ordering constraint as the OOP-730
            // `sendAutoJumpToLiveEvent` in the last-chapter branch.
            {
              cond: 'wasPausedWhileLive',
              actions: [
                'stop',
                'sendLiveOriginResumeEvent',
                'exitRewindMode',
                'setPlayLive',
                'setPlayingLiveEdge',
              ],
              target: 'LOADING',
            },
            // Rewind-content ad: part of the downloaded chapter — resume it
            // in place per the product ruling.
            {
              cond: 'isAd',
              target: 'PLAY_REQUESTED',
            },
            {
              target: 'LOADING',
            },
          ],
          ...COMMON_ACTIONS,
        },
      },
      ENDED: {
        on: {
          GO_TO_LIVE: {
            actions: ['stop', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
            target: 'LOADING',
          },
          SEEK_TIME: [
            {
              cond: 'hasSeekedToEndOfStandaloneChapter',
              actions: ['stop', 'resetResumePoint', 'handleRewindContentEnded'],
              target: 'PAUSED',
            },
            // [OOP-738] Seek onto the synthetic live chapter → hand off to live.
            {
              cond: 'isSeekToLiveChapter',
              // [OOP-731] analytics BEFORE exitRewindMode (episode → contentId).
              actions: ['stop', 'sendMp3SeekToLiveEvent', 'clearPlayingLiveEdge', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            {
              cond: 'hasSeekedPastLive',
              // [OOP-731] analytics BEFORE exitRewindMode (episode → contentId).
              actions: ['stop', 'sendMp3SeekToLiveEvent', 'resetElapsed', 'exitRewindMode', 'setPlayLive', 'setPlayingLiveEdge'],
              target: 'LOADING',
            },
            {
              // [OOP-737] An explicit user seek (including chapter taps, which fire
              // SEEK_TIME with the chapter's start time) signals intent to stay in
              // rewind. assignSeekTime sets isLiveMode:false, so subsequent
              // PAUSE/RESUME restores
              // the rewind offset instead of jumping back to live.
              actions: ['stop', 'assignSeekTime', 'assignCurrentChapterIndexWithOffset', 'saveResumePoint', 'clearPlayingLiveEdge'],
              target: 'LOADING',
            },
          ],
          REPORT_FAILURE: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          REPORT_PLAYBACK_FAILED: {
            target: '#failureWithRetry',
            actions: 'sendNativePlaybackFailedEvent',
          },
          ...COMMON_ACTIONS,
        },
      },
      FAILURE_WITH_RETRY: {
        id: 'failureWithRetry',
        entry: ['assignIncrementRetryCount', 'logRetry'],
        on: {
          ...COMMON_ACTIONS,
        },
        after: [
          {
            // iOS will immediately send a pause event after a failure. This will cancel the retry attempt.
            // The FAILURE_WITH_RETRY state needs to ignore the Pause event and we quickly transition to
            // WAITING_TO_RETRY so the user can pause the stream.
            target: 'WAITING_TO_RETRY',
            delay: 100,
          },
        ],
      },
      WAITING_TO_RETRY: {
        on: {
          PLAY: {
            target: 'LOADING',
          },
          PAUSE: {
            target: 'PAUSED',
            actions: 'pause',
          },
          STOP: {
            actions: ['stop', 'sendStopEvent'],
          },
          REPORT_PAUSED: {
            target: 'PAUSED',
          },
          ...COMMON_ACTIONS,
        },
        after: [
          {
            target: 'FAILURE',
            delay: (context) => getExponentialBackoffDelay(context.retryCount),
            // [OOP-734] Honor a context-supplied `maxRetryAttempts` override
            // when present, falling back to the MAX_RETRY_ATTEMPTS constant.
            // This is the configurable retry-cap path called for by the AC.
            cond: ({ retryCount = 0, maxRetryAttempts }) =>
              retryCount >= (maxRetryAttempts ?? MAX_RETRY_ATTEMPTS),
          },
          {
            actions: ['stop', 'loadAudio', sendParent({ type: 'PLAY' })],
            target: 'LOADING',
            delay: (context) => getExponentialBackoffDelay(context.retryCount),
            cond: ({ retryCount = 0, maxRetryAttempts }) =>
              retryCount < (maxRetryAttempts ?? MAX_RETRY_ATTEMPTS),
          },
        ],
      },
      FAILURE: {
        id: 'failure',
        entry: ['logPlaybackFailure', 'stop', 'sendPlaybackFailedEvent'],
        on: {
          ...COMMON_ACTIONS,
        },
        after: [
          {
            delay: AUTO_TRANSITION,
            target: 'PAUSE_REQUESTED',
          },
        ],
      },
      DESTROYED: {
        entry: ['stop', 'sendStopEvent', 'saveResumePoint'],
        type: 'final',
      },
    },
  },
  {
    services: {
      // [OOP-741] Wall-clock content poller. Runs its own re-rolling timer
      // (fresh jitter each tick, so clients don't fire in synchronized waves)
      // and sends REFRESH_CHAPTERS into the machine. Invoked on the PLAYING
      // compound state, so it survives every child transition and is torn
      // down only when playback stops. Decoupled from the transition
      // lifecycle, unlike the `after` timers it replaces.
      pollContent: () => (sendBack) => {
        let timeoutId: ReturnType<typeof setTimeout>;
        const scheduleNext = (): void => {
          timeoutId = setTimeout(() => {
            sendBack({ type: 'REFRESH_CHAPTERS' });
            scheduleNext();
          }, getJitteredInterval(SCHEDULE_UPDATE_INTERVAL, SCHEDULE_UPDATE_JITTER));
        };
        scheduleNext();
        return () => clearTimeout(timeoutId);
      },
      getChapters: async function (ctx) {
        const { episode } = ctx;
        if (!episode) {
          return ctx;
        }

        const chapters = [
          ...((await episode.getChapters({
            broadcastChapters: ctx.featureFlags?.broadcastChapters,
          })) ?? []),
        ];
        // [OOP-738] COPY the cached array (spread above) so pushing the synthetic
        // live chapter never mutates Episode.getChapters' cached list.
        // Synthetic live-edge chapter only exists for a show that is still on
        // air — mirrors the gate in getSchedule below so both chapter loaders
        // produce the same shape. A completed past show must not grow an
        // "On air" row.
        if (episode.isLiveOnAir()) {
          chapters.push(getLiveChapter(episode, chapters as Array<Chapter>));
        }
        // OOP-869 TODO: remove these console logs
        console.log('[MP3RewindStreamer] getChapters: loaded', chapters.length, 'chapters with fixture URLs:',
          (chapters as Array<Chapter>).map((c, i) => `[${i}] ${c.getAudioUrl() ?? 'NO URL'}`));

        ctx.chapters = chapters;
        // [OOP-741] Refresh the availability boundary the scrubber draws the
        // MP3-available region up to. Recomputed on every chapter poll so the
        // "drawn in" portion of the live-show scrub bar grows as new content
        // is received.
        ctx.availableContentEndOffset = getAvailableContentEndOffset(
          chapters as Array<Chapter>,
          episode,
        );
        return ctx;
      },
      getSchedule: async function (ctx) {
        const { playLive } = ctx;
        let playbacks = await ctx.personalizationProvider.getPlaybacks();

        // 'Back to live' feature (BROADCAST only)
        if (playLive) {
          ctx.requestedOffset = undefined;
          playbacks = {};
        }

        // Get the episode (BROADCAST only)
        if (ctx.streamType === 'BROADCAST') {
          const schedules = await ctx.station?.getSchedules();

          // Case 1: no episode yet (initial load / resume) — resolve it from the
          // requestedOffset. [OOP-738] When an episode is ALREADY loaded we keep
          // it: mid-session offsets live on the chapter timeline, which — with
          // fixture durations (OOP-794) — can fall outside the episode's real
          // broadcast window, so re-resolving would hop to the previous/next
          // episode and swap the whole chapter list mid-seek.
          if (ctx.requestedOffset && !ctx.episode) {
            const requestedOffset = ctx.requestedOffset;
            ctx.episode = await schedules?.getEpisode(requestedOffset * 1000);

            // [OOP-756] EpisodeList.getEpisode silently returns the schedule's
            // LAST episode when no entry covers the timestamp (see the
            // silent-fallback note in advanceToNextShow). Without this guard, an
            // out-of-range offset (stale deeplink, corrupted resume point) would
            // start playback at chapter 0 of the wrong show. Reject any resolved
            // episode that doesn't actually cover the requested timestamp —
            // future timestamps included — and jump to live instead.
            //
            // Gated on `validateOffsetOnLoad` (armed by assignDefaults on each
            // LOADING entry, disarmed after one use below) so it applies only
            // to the initial resolution of an externally-supplied offset —
            // NOT to UPDATING_SCHEDULE polls mid-playback, where flipping the
            // context to live without stopping the player would desync UI,
            // resume points, and audio. See the field's doc-comment.
            // Also require the resolved episode to be rewindable — the async
            // deeplink validator (isValidRewindTimestamp) already rejects
            // non-rewindable episodes and toasts; without the same check here
            // the machine would accept the offset the validator just told the
            // user was unavailable (e.g. a non-rewindable live game on an
            // otherwise rewindable station).
            if (
              ctx.validateOffsetOnLoad &&
              (!episodeCoversTimestamp(ctx.episode, requestedOffset) ||
                !ctx.episode?.isRewindable())
            ) {
              ctx.logger?.info(
                `[OOP-756] requestedOffset ${requestedOffset} is outside the available schedule — jumping to live`,
              );
              // [OOP-731] Deeplink-quality observability: the rejected offset
              // and the live fallback are otherwise invisible to analytics.
              ctx.analyticsProvider.sendEventToListener({
                type: PlayerMetric.STREAM_OFFSET_OUT_OF_COVERAGE,
                eventDetails: {
                  stationId: ctx.item.getId(),
                  stationName: ctx.item.getTitle(),
                  streamerType: ctx.contentType,
                  // The rejected absolute offset — IEventDetails' existing
                  // currentPosition field (avoids widening the shared shape).
                  currentPosition: requestedOffset,
                },
              });
              ctx.requestedOffset = undefined;
              ctx.episode = undefined;
              ctx.playLive = true;
              ctx.playingLiveEdge = true;
              playbacks = {};
            }
          }

          // [OOP-756] One-shot: subsequent getSchedule invocations (the
          // UPDATING_SCHEDULE poll) skip the guard until the next LOADING
          // entry re-arms it.
          ctx.validateOffsetOnLoad = false;

          // Case 2: we don't have an episode, default to live episode
          if (!ctx.episode) {
            ctx.episode = await schedules?.getEpisode(Date.now());
          }

          // Case 3: we already have an episode, keep it
          // no-op
        }

        const isStandaloneChapter = ctx.streamType === 'STANDALONE_CHAPTER';

        const entity = isStandaloneChapter ? ctx.standaloneChapter : ctx.episode;

        // We should always have an episode now, this is just for typechecking (no-op)
        if (!entity) {
          // const message =  `BUG: No ${isStandaloneChapter ? 'standaloneChapter' : 'episode'} found while playing SSR`;
          // TODO: send message error to future logging service
          return ctx;
        }

        // Normalize the resume point to something we can use. [OOP-738] Only
        // when we DON'T already have a requestedOffset: an existing one was set
        // by our own seek/advance actions and is already an absolute offset on
        // the chapter timeline. Re-running it through normalizeResumePoint
        // would validate it against the episode's real broadcast window, which
        // fixture-timeline offsets (OOP-794) can legitimately fall outside of —
        // the reset to `undefined`/episode-start is what restarted chapters
        // from 0:00 after a seek.
        if (ctx.requestedOffset === undefined && !playLive) {
          ctx.requestedOffset = normalizeResumePoint({
            resumePoint: playbacks[entity.getId()],
            entity,
            streamType: ctx.streamType,
          });
        }

        // const message =  `Resume point for entity ${entity.getId()} for SSR is ${ctx.requestedOffset}`;
        // TODO: send message error to future logging service

        // Throw an error if trying to play non-rewindable, non-live content (like a game)
        // TODO: [CCS-1762] this check should be part of loadAudio but that is currently an action not a service. We should refactor that to be a service and update the machine accordingly.
        if (!entity.isRewindable() && entity.getEndTimeMillis() < Date.now()) {
          // TODO: send error to future logging service
          throw new Error(NON_REWINDABLE_ERROR);
        }

        // Update episode chapters
        if (ctx.episode) {
          const playback = playbacks[entity.getId()];
          // [OOP-738] COPY — Episode.getChapters returns its cached array by
          // reference; pushing the synthetic live chapter into it grew the show
          // by one fixture-stamped phantom chapter per LOADING pass (see the
          // same note in the getChapters service above).
          const chapters = [
            ...((await ctx.episode.getChapters({
              playback,
              broadcastChapters: ctx.featureFlags?.broadcastChapters,
            })) ?? []),
          ];
          // [OOP-738] Copy the cached array (spread above) so pushing the
          // synthetic live chapter never mutates Episode.getChapters' cached
          // list. Append it only when the show is still on air (matches the
          // getChapters gate) so both loaders produce the same length-1/length-2
          // shape the guards rely on.
          if (ctx.episode.isLiveOnAir()) {
            chapters.push(getLiveChapter(ctx.episode, chapters as Array<Chapter>));
          }
          // OOP-869 TODO: remove these console logs
          console.log('[MP3RewindStreamer] getSchedule: loaded', chapters.length, 'chapters with fixture URLs:',
            (chapters as Array<Chapter>).map((c, i) => `[${i}] ${c.getAudioUrl() ?? 'NO URL'}`));

          ctx.chapters = chapters;
          // [OOP-741] Mirror of the getChapters assignment — both loaders
          // must produce the same availability boundary shape.
          ctx.availableContentEndOffset = getAvailableContentEndOffset(
            chapters as Array<Chapter>,
            ctx.episode,
          );
        } else if (ctx.standaloneChapter) {
          // For a standalone chapter there is only one chapter — the entity itself.
          const chapter = new Chapter({
            duration: ctx.standaloneChapter.getDuration(),
            startOffset: ctx.standaloneChapter.getStartOffset(),
            title: 'Chapter',
            transcript: ctx.standaloneChapter.getTranscript(),
          });
          console.log('[MP3RewindStreamer] getSchedule (standalone): 1 chapter with URL:', chapter.getAudioUrl()); // OOP-869 TODO: remove these console logs
          ctx.chapters = [chapter];
        }

        return ctx;
      },
      loadStationSources: async function (ctx) {
        const source = (
          await loadSourcesForItem({
            ...ctx,
            // There is an edge case when playing a past BROADCAST rewind episode
            // that episode has a null streamUrl so use the station's streamUrl
            item: ctx.streamType === 'BROADCAST' ? (ctx.station as TPlayableObject) : ctx.item,
          })
        )?.find((p) => p.type === 'm3u8');

        return {
          ...ctx,
          source,
        };
      },
      // [OOP-729] Resolves the next chronological show in the station's
      // schedule by looking up the episode whose time range starts immediately
      // after the current episode's end. Sets `ctx.episode` to the new show,
      // clears `requestedOffset`, and clears `chapters` so the standard LOADING
      // flow re-fetches them. On failure (no next show, schedule unavailable)
      // throws so the onError branch can fall back to PAUSED.
      advanceToNextShow: async function (ctx) {
        if (!ctx.episode || !ctx.station) {
          throw new Error('advanceToNextShow: missing episode or station in context');
        }
        const currentEndSec = ctx.episode.getEndTimeMillis() / 1000;
        const currentId = ctx.episode.getId();
        const schedules = await ctx.station.getSchedules();
        // Resolve strictly-next-by-start-time from the schedule entries rather
        // than probing getEpisode(end+1): that timestamp lookup silently
        // returns the LAST entry when no entry covers the boundary, which
        // either threw here (same id → PAUSED) or skipped shows across gaps.
        // Entries are DESCENDING (newest first); gap-filler dummies have id ''.
        const entries: Array<{ id?: string; startDateTimeSeconds?: number }> =
          schedules?.data ?? [];
        let nextIdx = -1;
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          const startSec = entry?.startDateTimeSeconds ?? 0;
          // 1s fudge covers EpisodeList's :59→:00 end-time normalization.
          if (!entry?.id || entry.id === currentId || startSec < currentEndSec - 1) {
            continue;
          }
          if (nextIdx === -1 || startSec < (entries[nextIdx].startDateTimeSeconds ?? 0)) {
            nextIdx = i;
          }
        }
        if (nextIdx === -1) {
          throw new Error('advanceToNextShow: no next show available in schedule');
        }
        // Tolerance of 24h covers normal schedule gaps (overnight breaks)
        // without advancing to a random far-future entry.
        const NEXT_SHOW_TOLERANCE_SEC = 24 * 60 * 60;
        const nextStartSec = entries[nextIdx].startDateTimeSeconds ?? 0;
        if (nextStartSec - currentEndSec > NEXT_SHOW_TOLERANCE_SEC) {
          throw new Error(
            'advanceToNextShow: schedule returned an episode outside the expected time window',
          );
        }
        const nextEpisode = (await schedules.getObject(
          nextIdx,
        )) as IMp3RewindStationMachineContext['episode'];
        if (!nextEpisode) {
          throw new Error('advanceToNextShow: no next show available in schedule');
        }
        return {
          ...ctx,
          episode: nextEpisode,
          // Start the next show from the beginning. A completed next show gets
          // an explicit offset (chapter 0 resolves from it) instead of relying
          // on getSchedule's normalizeResumePoint backfill; a live-on-air next
          // show keeps the undefined-offset live handoff — its chapter list
          // can be sentinel-only (no real chunks yet), where a forced offset
          // would match a URL-less chapter and loop on NO_AUDIO_URL.
          requestedOffset: nextEpisode.isLiveOnAir?.()
            ? undefined
            : nextEpisode.getStartTimeSeconds?.(),
          chapters: undefined,
          currentChapterIndex: undefined,
          elapsed: 0,
          elapsedBeforeAdvance: undefined,
        };
      },
    },
    guards: {
      // Check if playing non-live content
      isPlayingRewindContent: ({ metadata }) => {
        return !metadata?.isLive;
      },
      isFinishedStandaloneChapter: (ctx) => {
        const standalone = !!ctx.standaloneChapter;

        return standalone && isPlayingAfterShowEnd(ctx);
      },
      isPlayingAfterEntityEnd: (ctx) => isPlayingAfterShowEnd(ctx),
      isAd: ({ metadata }) => isContentTypeAd(metadata?.contentType),
      // [OOP-730] True when, on a still-live show, the user has played past
      // the end of the last available MP3 chapter — i.e. caught up to live.
      // `getLiveChapter` appends a synthetic live-edge chapter at the end of
      // `ctx.chapters`, so the last real MP3 chapter sits at index
      // `chapters.length - 2`. This invariant holds for BOTH `getChapters`
      // and `getSchedule` services — see [OOP-731] note inside `getSchedule`
      // for why the synthetic tail must be appended in both loaders.
      // We detect end-of-content by comparing the current absolute
      // wall-clock playback position against that chapter's absolute end
      // time (startOffset + duration), NOT by comparing `ctx.elapsed` to a
      // single chapter's duration: per mapPlayerTime in rewindUtils.ts,
      // `elapsed` is "seconds since episode start", which makes the
      // chapter-relative comparison incorrect (the guard would have fired
      // at the START of the last chapter, not the end).
      //
      // The `isLiveOnAir()` check makes this case mutually exclusive with
      // OOP-729's `isFinishedCompletePastShow` below: live shows jump to the
      // live edge here; past shows auto-advance to the next show there. NOTE:
      // `isLiveOnAir()` itself is `endTimeMillis > Date.now()` — an
      // instantaneous flip, not a window. The practical race window is the
      // REPORT_TIME_UPDATE cadence (~1s): if a show ends between two ticks,
      // the next guard evaluation sees `isLiveOnAir() = false` and OOP-729's
      // branch takes over, auto-advancing to the next show instead of
      // jumping to the live edge of the (now ended) current one. Behavior
      // is defensible (the show is over) but worth flagging.
      //
      // Lag note: this guard reads `ctx.elapsed`, which is updated by the
      // `assignTime` action that fires in the default REPORT_TIME_UPDATE
      // branch — i.e. the guard sees the PREVIOUS tick's elapsed when it
      // evaluates. The lag is ~1s and invisible in practice, but it's why
      // the boundary check uses strict `>` rather than `>=`.
      //
      // Server-side caveat: if the user catches up to the last chapter the
      // client has fetched but the backend has minted a newer chunk that
      // hasn't been pulled in yet, this guard fires "caught up to live"
      // prematurely. The `+10s` spacer baked into `getLiveChapter` is the
      // only buffer. Polling work in OOP-741 should narrow this window.
      isFinishedLastAvailableChapter: (ctx) => {
        const { chapters, elapsed, episode, playLive, isLiveMode } = ctx;
        if (playLive || isLiveMode) return false
        if (!chapters || chapters.length < 2 || !episode) return false

        // Only applies to still-live shows. Past shows are handled by
        // OOP-729's `isFinishedCompletePastShow` below.
        if (!episode.isLiveOnAir()) return false;
        const lastReal = chapters[chapters.length - 2];
        const liveEdgeChapter = chapters[chapters.length - 1];
        if (!lastReal || !liveEdgeChapter) return false

        const episodeStartSec = episode.getStartTimeMillis() / 1000;
        const currentWallClockSec = episodeStartSec + (elapsed ?? 0);
        const liveEdgeStartAbsSec = liveEdgeChapter.getStartOffset();
        if (liveEdgeStartAbsSec <= 0) return false

        // [OOP-730] No metadata.isLive term here: during MP3 chapter playback
        // metadata.isLive is always FALSE (the chapter loadAudio branch sets
        // it), and once it's true we're already at the live edge — so an
        // `&& isLive` conjunct (added during an OOP-738-era rework) made this
        // guard unsatisfiable in both directions and silently killed the
        // time-update path. REPORT_ENDED / REPORT_PAUSED cover the same
        // moment, but this branch is the backstop when the native player
        // emits neither (e.g. a stalled file that keeps ticking time updates
        // into the live-edge window).
        return currentWallClockSec >= liveEdgeStartAbsSec;
      },
      // [OOP-734] Allows one retry attempt on UPDATING_CHAPTERS.onError before
      // falling back to IS_PLAYING. Cap at 1 keeps the worst case to two
      // attempts total — enough to absorb transient network blips without
      // pinging the chapter API in a tight loop on persistent failures.
      canRetryChapterFetch: ({ chapterRetryCount = 0 }) => chapterRetryCount < 1,
      // [OOP-737] True if the user entered Rewind from live and hasn't explicitly
      // committed to rewindable content (e.g. by seeking into a chapter).
      // [OOP-737] Pause-time liveness: `isLiveMode` is written only by
      // loadAudio (live stream → true, chapter mp3 → false) and by explicit
      // seeks (→ false), and nothing mutates it on PAUSE — so at PAUSED.PLAY
      // it is a faithful record of what was playing when the user paused.
      // `playingLiveEdge` is the belt for the brief window where a live
      // transition has been decided but the live stream hasn't loaded yet.
      wasPausedWhileLive: ({ isLiveMode, playingLiveEdge }) =>
        !!isLiveMode || !!playingLiveEdge,
      // [OOP-729] True when the user has played past the end of a complete
      // (non-live) show. `ctx.elapsed` is "seconds since the episode's start"
      // (see mapPlayerTime in rewindUtils.ts), so we compare it against the
      // episode's full duration — the same shape as the sibling
      // `isPlayingAfterEntityEnd` guard. The non-live check is what makes
      // this case distinct: for a complete past show, end-of-content should
      // auto-advance to the next show; for a live show, the equivalent
      // moment is "caught up to live" (handled by OOP-730's
      // `isFinishedLastAvailableChapter` above).
      //
      // Branch ordering in REPORT_TIME_UPDATE puts this BEFORE the broader
      // `isPlayingAfterEntityEnd` branch so the auto-advance fires instead
      // of the generic schedule-refresh.
      // [OOP-729] REPORT_ENDED-side companion to isFinishedCompletePastShow.
      // Arriving via REPORT_ENDED means the native player finished the current
      // chapter — combined with hasNextChapter=false (evaluated earlier in the
      // REPORT_ENDED branch order) it means the last chapter of the current
      // show is done. No elapsed check needed: REPORT_ENDED is the
      // authoritative end-of-content signal.
      isCompletePastBroadcast: (ctx) => {
        const { streamType, episode, standaloneChapter } = ctx;
        // A live-stream session can carry a stale past `episode` (e.g. after
        // an uncovered-offset live fallback keeps the resolved episode).
        // REPORT_ENDED there is a live-stream event, not end-of-past-show.
        if (ctx.isLiveMode || ctx.playLive) return false;
        if (streamType !== 'BROADCAST') return false;
        if (standaloneChapter) return false;
        if (!episode) return false;
        if (episode.isLiveOnAir?.()) return false;
        return true;
      },
      isFinishedCompletePastShow: (ctx) => {
        const { episode, elapsed, streamType, isLiveMode, playingLiveEdge } = ctx;
        // Only BROADCAST streams populate `ctx.episode` from a station
        // schedule (see getSchedule service). For STANDALONE_CHAPTER /
        // PODCAST streamTypes there's no "next show" concept to advance to.
        if (streamType !== 'BROADCAST') return false;
        if (!episode) return false;
        if (isLiveMode || playingLiveEdge || episode.isLiveOnAir?.()) return false;
        const duration = episode.getDuration?.() ?? 0;
        if (duration <= 0) return false;
        return (elapsed ?? 0) > duration;
      },
      // At the live edge, the current show ending (no longer on air) means the
      // broadcast rolled to the next show. Refresh the schedule while playback
      // continues instead of stopping/reloading. Self-terminates: once the new
      // on-air show loads, isLiveOnAir() is true again.
      isLiveShowRolledOver: (ctx) => {
        const { episode, isLiveMode, playingLiveEdge, streamType } = ctx;
        if (streamType !== 'BROADCAST') return false;
        if (!(isLiveMode || playingLiveEdge)) return false;
        if (!episode) return false;
        return !episode.isLiveOnAir?.();
      },
      // [OOP-731] Mirrors setIsLiveAndRequestedOffset's internal stale-offset
      // decision so the auto-route-to-live case can carry analytics. True when
      // the native continuePlaying hint is NOT an explicit go-live but its
      // playlistNextStartTime falls outside the current episode's window —
      // the action will route to live.
      isContinuePlayingStaleToLive: (ctx, e) => {
        if (ctx.isLiveMode) return false;
        return isStaleContinueOffsetToLive(ctx, e);
      },
      isNotLiveMode: ({ isLiveMode }) => !isLiveMode,
      // TODO: Validate that this is actually right to default to true - might be better to switch over streamType
      isRewindable: ({ episode }) => episode?.isRewindable() ?? true,
      isNonRewindableContent: (ctx) => {
        return ctx.contentType === EContentType.Rewind && !ctx.episode?.isRewindable();
      },
      isNonRewindableContentError: (_, event) => {
        const { message } = event.data as Error;
        return message === NON_REWINDABLE_ERROR;
      },
      isStandaloneChapter: (ctx) => !!ctx.standaloneChapter,
      shouldStandaloneChapterStop: (ctx) => {
        // when a standalone chapter ends the player still attempts a tarzan swing
        // preroll ads can exist, so we want to make sure we only stop if the contentType is content.
        // beyond that a standaloneChapter and requestedOffset exiting tell us that we want to stop.
        const { metadata, requestedOffset, standaloneChapter } = ctx;
        return !!standaloneChapter && !!requestedOffset && metadata?.contentType === 'content';
      },
      hasSeekedToEndOfStandaloneChapter: (ctx, { time }) => {
        const { standaloneChapter } = ctx;
        
        if (!standaloneChapter) {
          return false;
        }

        const startTs = normalizeResumePoint({
          resumePoint: time,
          entity: standaloneChapter,
          streamType: 'STANDALONE_CHAPTER',
        });

        if (!startTs) {
          return false;
        }

        const endTs = standaloneChapter.getEndTimeSeconds();

        return startTs >= endTs;
      },
      // [OOP-765] True when the user seeked past the live edge on a live show
      // (requested wall-clock time is beyond now) — hand off to live.
      hasSeekedPastLive: (ctx, { time }) => {
        const { episode } = ctx;
        if (!episode) return false;
        const startTime = episode.getStartTimeSeconds();
        const endTime = episode.getEndTimeSeconds();
        const now = Date.now() / 1000;
        const isLiveShow = startTime < now && endTime > now;
        return isLiveShow && (startTime + time > now);
      },
      // [OOP-738] True when the seek target lands in the chapter whose MP3 is
      // already loaded, so we can seek inside the file instead of reloading a
      // different one. Guards run before assignSeekTime, so derive the target
      // chapter from the event's `time` — clamped to the show timeline exactly
      // the way assignSeekTime will clamp it, and converted via the
      // chapter-timeline anchor (getOffsetFromSeekTime), NOT normalizeResumePoint,
      // whose real-broadcast-window checks reject fixture-timeline offsets.
      isSeekInCurrentChapter: (ctx, { time }) => {
        const { chapters, currentChapterIndex } = ctx;
        // [OOP-765] Never take the MP3 chapter fast-path while the live stream is
        // loaded — seeking is an HLS/live concern there, not an in-file seek.
        if (ctx.isLiveMode) {
          return false;
        }
        if (currentChapterIndex === undefined || !chapters?.length) {
          return false;
        }
        const showDuration = getChaptersTotalDuration(chapters);
        const clamped =
          showDuration > 0 ? Math.min(Math.max(0, time), showDuration) : Math.max(0, time);
        const target = getOffsetFromSeekTime(chapters, clamped);
        if (target === undefined) {
          return false;
        }
        // NO_CHAPTER_MATCH can never equal a defined currentChapterIndex, so a
        // seek target that predates every chapter correctly falls through to
        // the reload path instead of masquerading as an in-chapter seek.
        const targetIdx = getChapterIndexFromOffset(chapters, target);
        // OOP-869 TODO: remove this diagnostic log
        console.log(
          `[MP3RewindStreamer] guard isSeekInCurrentChapter: time=${time}, clamped=${clamped}, target=${target}, targetIdx=${targetIdx}, currentIdx=${currentChapterIndex} -> ${targetIdx === currentChapterIndex}`,
        );
        return targetIdx === currentChapterIndex;
      },
      // [OOP-738] True when the seek target lands on the synthetic live chapter
      // (appended by getLiveChapter at length-1). Per product, this hands off to
      // live rather than playing the live chapter as a file. Uses the RAW event
      // time (no show-duration clamp): the live chapter's startOffset sits 10s
      // PAST the end of the summed chapter durations, so clamping would make the
      // live row unreachable.
      isSeekToLiveChapter: (ctx, { time }) => {
        const { chapters } = ctx;
        // [OOP-765] Already on the live stream — nothing to hand off to.
        if (ctx.isLiveMode) {
          return false;
        }
        if (!chapters || chapters.length < 2) {
          return false;
        }
        // [OOP-963] Handoff requires an on-air show AND the duration-0 sentinel
        // at length-1 — absent on completed shows (OOP-765 gates the append).
        if (!ctx.episode?.isLiveOnAir()) {
          return false;
        }
        if (chapters[chapters.length - 1].getDuration() !== 0) {
          return false;
        }
        const target = getOffsetFromSeekTime(chapters, time);
        if (target === undefined) {
          return false;
        }
        // NO_CHAPTER_MATCH (-1) is always < length - 1, so no-match → false.
        const result = getChapterIndexFromOffset(chapters, target) >= chapters.length - 1;
        // OOP-869 TODO: remove this diagnostic log
        console.log(
          `[MP3RewindStreamer] guard isSeekToLiveChapter: time=${time}, target=${target}, liveChapterStart=${chapters[chapters.length - 1]?.getStartOffset()}, -> ${result}`,
        );
        return result;
      },
      // [OOP-738] True when there is a next REAL chapter to auto-advance to.
      // On a LIVE show the synthetic live chapter sits at length-1, so the last
      // real chapter is length-2; its ending is left to the go-live path /
      // OOP-730. On a completed PAST show there is no synthetic tail
      // (getChapters/getSchedule gate the getLiveChapter push on isLiveOnAir),
      // so the last real chapter is length-1.
      hasNextChapter: (ctx) => {
        const { chapters, currentChapterIndex } = ctx;
        // [OOP-765] Chapter auto-advance is a rewind concern only; the live
        // stream has no "next chapter". (Already effectively false in live mode
        // since currentChapterIndex pins to the synthetic tail; explicit for clarity.)
        if (ctx.isLiveMode) {
          return false;
        }
        if (!chapters || currentChapterIndex === undefined) {
          return false;
        }
        // [OOP-729] Tail-aware last-real index, derived from the ARRAY (see
        // lastRealChapterIdx): the old unconditional `length - 2` made the
        // second-to-last chapter of a past show report "no next chapter",
        // and a wall-clock isLiveOnAir() check races the sentinel's
        // fetch-time lifecycle when a show ends mid-chapter.
        return currentChapterIndex < lastRealChapterIdx(chapters);
      },
      // [OOP-738] The native player reports a PAUSE at the natural end of a chapter
      // MP3 (not always a clean 'ended'). This guard distinguishes that end-of-file
      // pause from a real user pause: there must be a next real chapter AND the
      // last reported position (elapsed) must be at/near the current chapter's end.
      // Lets REPORT_PAUSED auto-advance instead of stopping. Uses elapsed (the same
      // value mapMp3PlayerTime maintains) rather than the post-end player position,
      // which some native players reset. The ~2s grace covers the gap between the
      // last time update and the actual end.
      // A REPORT_PAUSED that reaches PLAYING is PLAYER-originated — user pauses
      // route through the PAUSE event → PAUSE_REQUESTED, never here. So on a
      // non-last chapter this is the natural end-of-file (content + trailing ad,
      // so `elapsed` is at/past the content `chapterEnd`) → auto-advance. A
      // mid-chapter stall would have elapsed well below chapterEnd and won't
      // trip. (Same player-originated-pause rationale as isFinishedLastRealChapter,
      // which uses chapterStart+2; non-last keeps chapterEnd-2 so a stall can't
      // skip a chapter.) [OOP-738]
      isFinishedNonLastChapter: (ctx) => {
        const { chapters, currentChapterIndex, elapsed } = ctx;
        if (ctx.isLiveMode) {
          return false;
        }
        if (!chapters || currentChapterIndex === undefined || elapsed === undefined) {
          return false;
        }
        // [OOP-729] Tail-aware last-real index derived from the ARRAY (see
        // lastRealChapterIdx — wall-clock isLiveOnAir() races the sentinel's
        // fetch-time lifecycle). Pre-fix, a past show's second-to-last
        // chapter PAUSE-at-EOF fell through to plain PAUSED.
        if (currentChapterIndex >= lastRealChapterIdx(chapters)) {
          return false;
        }
        const chapterEnd =
          getChapterTimelineStart(chapters, currentChapterIndex) +
          (chapters[currentChapterIndex]?.getDuration() ?? 0);
        const result = elapsed >= chapterEnd - 2;
        if (result) {
          // OOP-869 TODO: remove this diagnostic log
          console.log(
            `[MP3RewindStreamer] guard isFinishedNonLastChapter: elapsed=${elapsed} >= chapterEnd(${chapterEnd})-2 → auto-advance from idx=${currentChapterIndex}`,
          );
        }
        return result;
      },
      // [OOP-738] REPORT_PAUSED counterpart for the LAST real chapter
      // (length - 2; the synthetic live chapter sits at length - 1) on a
      // still-live show: the end-of-file pause means we caught up to live.
      // Same end-detection as isFinishedNonLastChapter; the live handoff itself
      // reuses OOP-730's exitRewindMode/setPlayLive actions unchanged.
      // [OOP-729] Past-show mirror of isFinishedLastRealChapter: the native
      // player can signal the LAST chapter's natural end as a PAUSE instead of
      // REPORT_ENDED. On a live show that means "caught up to live"
      // (isFinishedLastRealChapter below); on a completed PAST show it means
      // "show finished" → auto-advance to the next chronological show.
      // Without this branch the listener is stranded in PAUSED at the end of
      // every past show whose player emits the PAUSE-at-EOF quirk.
      // KNOWN TRADEOFF (same as isFinishedNonLastChapter /
      // isFinishedLastRealChapter): an OS-originated pause (interruption,
      // unplug) arriving as REPORT_PAUSED during the last chapter's baked-in
      // trailing ad (elapsed >= chapterEnd-2 throughout the tail) is read as
      // EOF and auto-advances, eating the pause. User pauses are unaffected
      // (they route PAUSE → PAUSE_REQUESTED).
      isFinishedLastChapterOfPastShow: (ctx) => {
        const { chapters, currentChapterIndex, elapsed, episode, streamType, standaloneChapter } = ctx;
        if (ctx.isLiveMode || ctx.playLive) {
          return false;
        }
        if (streamType !== 'BROADCAST' || standaloneChapter) {
          return false;
        }
        if (!chapters || currentChapterIndex === undefined || elapsed === undefined) {
          return false;
        }
        if (!episode || episode.isLiveOnAir?.()) {
          return false;
        }
        // Last REAL chapter, array-derived: on a freshly-ended show the
        // sentinel can linger in ctx.chapters until the next refresh, so
        // length-1 alone would point at the sentinel and never match the
        // listener's actual chapter.
        if (currentChapterIndex !== lastRealChapterIdx(chapters)) {
          return false;
        }
        // Same castfire-drift handling as isFinishedLastRealChapter: the real
        // MP3 length can run >2s short of the API duration (a show's final
        // chapter has no ad tail), so an end-anchored gate misses and strands
        // the listener in PAUSED. Only sanity-check we've entered the chapter.
        const chapterStart = getChapterTimelineStart(chapters, currentChapterIndex);
        return elapsed >= chapterStart + 2;
      },
      isFinishedLastRealChapter: (ctx) => {
        const { chapters, currentChapterIndex, elapsed, episode } = ctx;
        if (ctx.isLiveMode) {
          return false;
        }
        if (
          !chapters ||
          chapters.length < 2 ||
          currentChapterIndex === undefined ||
          elapsed === undefined
        ) {
          return false;
        }
        if (!episode?.isLiveOnAir()) {
          return false;
        }
        // Must be on the last REAL chapter (synthetic live sentinel is length-1).
        if (currentChapterIndex !== chapters.length - 2) {
          return false;
        }
        // [OOP-738] A REPORT_PAUSED that reaches PLAYING is PLAYER-originated —
        // user pauses route through the PAUSE event → PAUSE_REQUESTED, never
        // here. On the last real chapter of a live show a player-originated
        // pause means the content ran out (caught up to live), so hand off to
        // live. Deliberately NOT gated on `elapsed >= chapterEnd - 2`: that
        // compared against the API-reported duration, which the real castfire
        // MP3 length differs from by more than 2s, so the guard missed and the
        // player got stuck in PAUSED at the end of the show. We only sanity-
        // check that we've actually entered the last chapter (played at least a
        // few seconds of it) to reject a spurious very-early pause.
        const chapterStart = getChapterTimelineStart(chapters, currentChapterIndex);
        return elapsed >= chapterStart + 2;
      },
    },
    actions: {
      // Set default metadata so we have something to show while we load the audio
      assignDefaults: assign(({ item, metadata, firstLoadTime, analyticsProvider, requestedOffset }) => {
        if (!metadata) {
          // Initial load
          analyticsProvider.sendEventToListener({
            type: PlayerMetric.STREAM_LOAD,
            eventDetails: {
              stationId: item.getId(),
              stationName: item.getTitle(),
              streamerType: EContentType.Rewind,
              isNativeErrorHandling: true,
            },
          });
          bufferingEventCounter.init(analyticsProvider, {
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: EContentType.Rewind,
          });
        }
        return {
          firstLoadTime: firstLoadTime || Date.now(),
          datumTimeProtected: false,
          // [OOP-756] Arm the one-shot out-of-range offset guard for the
          // getSchedule call this LOADING pass is about to make.
          validateOffsetOnLoad: true,
          metadata: metadata ? {
            ...metadata,
            datumTime: metadata.datumTime ? requestedOffset ?? metadata.datumTime : undefined,
          } : {
            image: item.getImageSquare(),
            station: item.getTitle(),
          },
        };
      }),
      assignRetryCountToZero: assign({
        retryCount: 0,
      }),
      assignIncrementRetryCount: assign({
        retryCount: ({ retryCount = 0 }) => retryCount + 1,
      }),
      // [OOP-734] Chapter-fetch retry counter is separate from `retryCount`
      // (which tracks playback failures). Distinct so the chapter-list
      // retry/escalation logic doesn't interfere with the player's own
      // FAILURE_WITH_RETRY → WAITING_TO_RETRY backoff.
      incrementChapterRetryCount: assign({
        chapterRetryCount: ({ chapterRetryCount = 0 }) => chapterRetryCount + 1,
      }),
      resetChapterRetryCount: assign({
        chapterRetryCount: 0,
      }),
      // Add station and episode to history so both are accounted for in recents
      addToHistory: (ctx) => {
        const stationId = ctx.item.getId();
        const episodeId = ctx.episode?.getId();
        const standaloneChapterId = ctx.standaloneChapter?.getId();
        const contentIdsToAddToHistory = [];
        if (stationId) {
          contentIdsToAddToHistory.push(stationId);
        }
        if (episodeId) {
          contentIdsToAddToHistory.push(episodeId);
        }
        if (standaloneChapterId) {
          contentIdsToAddToHistory.push(standaloneChapterId);
        }

        // to avoid duplication of ids
        const uniqueContentIdsToAddToHistory = [...new Set(contentIdsToAddToHistory)];
        if (
          uniqueContentIdsToAddToHistory.length > 0 &&
          !ctx.personalizationProvider.isInTopOfHistoryStack(uniqueContentIdsToAddToHistory)
        ) {
          void ctx.personalizationProvider.addToHistory(uniqueContentIdsToAddToHistory);
        }
      },
      // [CCS-3193]: Type coercion used temporarily to fix issue
      // Come back and fix IRewindProviderContext
      // May need to remove the RewindProviderContext type from the IRewindProvider interface
      addEpisodeToHistory: (ctx, event) => {
        const data = event.data;
        const episodeId = data.episode?.getId();
        if (episodeId) {
          if (!ctx.personalizationProvider.isInTopOfHistoryStack(episodeId)) {
            void ctx.personalizationProvider.addToHistory([episodeId]);
          }
        }
      },
      assignScheduleResponse: assign(({ chapters, episode }, { data }) => ({
        ...data,
        chapters: data?.chapters ?? chapters,
        episode: data?.episode ?? episode,
      })),
      assignChapterResponse: assign((_, { data }) => ({
        chapters: data?.chapters,
        availableContentEndOffset: data?.availableContentEndOffset,
      })),
      // Perhaps a little too generic, this is only used to assign source aka the URL to play from
      assignRewindContext: assign((ctx, { data }) => {
        return { ...ctx, ...data };
      }),
      // [OOP-729] Spread the new episode / cleared offset / cleared chapters
      // from the advanceToNextShow service result onto context. The standard
      // LOADING flow on the next `#loading` entry will re-fetch sources +
      // schedule + chapters for the new episode.
      //
      // We also explicitly clear `metadata` here so the previous show's
      // `metadata.isLive` / `metadata.contentType` don't linger between
      // `#loading` entry and `LOADING_SCHEDULE.onDone` (which overwrites
      // metadata via `assignMetadata`). No guard reads metadata during that
      // window today, but clearing keeps the boundary explicit — if a
      // REPORT_METADATA event arrived during the window, `assignMetadata`
      // would merge against undefined rather than stale entity data.
      assignAdvancedShow: assign((_, { data }) => ({
        ...data,
        metadata: undefined,
      })),
      // Gets new metadata from AVPlayer/Exoplayer every second, then merge that with old metadata we may need,
      // update ctx.metadata, then in "onTransition" we send metadata to PlayerStateConnector then to Recoil atoms
      assignMetadata: assign((ctx, e) => {
        const { metadata, episode, standaloneChapter, item, logger, isLiveMode, datumTimeProtected } = ctx;

        const retVal: Partial<IMp3RewindStationMachineContext> = {};

        const entity = standaloneChapter ?? episode;

        const newMetadata = 'metadata' in e ? e.metadata : undefined;

        // Takes care of edgecase where data for title is incorrect from manifest
        if (newMetadata?.songOrShow) {
          newMetadata.songOrShow = '';
          newMetadata.artist = '';
        }

        const seekDatumTime = datumTimeProtected ? metadata?.datumTime : undefined;

        retVal.metadata = mergeMetadata({
          existingMetadata: metadata,
          newMetadata,
          logger,
          entity,
          item,
        });

        if (seekDatumTime !== undefined) {
          retVal.metadata.datumTime = seekDatumTime;
        }

        // Once the stream reports its actual datumTime, lock it to filter
        // segment noise. Only accept the stream's value if it's close to our
        // computed target (segment boundary correction). If it's far off
        // (e.g., stream reports playlist start on chapter seeks), keep ours.
        if (!datumTimeProtected && newMetadata?.datumTime) {
          if (metadata?.datumTime) {
            const diff = Math.abs(metadata.datumTime - newMetadata.datumTime);
            if (diff > 30) {
              retVal.metadata.datumTime = metadata.datumTime;
            }
          }
          retVal.datumTimeProtected = true;
        }

        retVal.contentType =
          entity?.isRewindable() ?? true ? EContentType.Rewind : EContentType.RewindNoReplay;

        if (!isLiveMode) {
          retVal.metadata = {
            ...retVal.metadata,
            isLive: false,
            contentType: newMetadata?.contentType,
          };
        }

        // [OOP-737] At the live edge of a still-broadcasting show, MP3 chunks
        // from the rewind backend don't carry the HLS `X-IS-LIVE` tag the live
        // stream emits — so the player layer reports metadata.isLive=false
        // even when the listener is audibly at live. Force it true while
        // `ctx.playingLiveEdge` is set so the UI's `playerContentIsLiveState`
        // selector renders the live indicator instead of the "back to live"
        // button. Set/cleared by `setPlayingLiveEdge` / `clearPlayingLiveEdge`
        // alongside `setPlayLive`.
        // NOTE: must run after the `!isLiveMode` block above — `loadAudio` sets
        // `isLiveMode` later in the same onDone action sequence, so `isLiveMode`
        // is still the old (false) value when `assignMetadata` first runs on
        // LOADING_SCHEDULE.onDone. Without this ordering, `!isLiveMode` would
        // override the live indicator even when `playingLiveEdge` is true.
        if (ctx.playingLiveEdge) {
          retVal.metadata = { ...retVal.metadata, isLive: true };
        }

        // [OOP-740] The scrub bar spans the whole show, so its duration must be
        // the sum of all chapter durations — not the per-chapter MP3 length the
        // native player reports. Keeps the cumulative `elapsed` (mapMp3PlayerTime)
        // and the duration on the same continuous show timeline. (contentType is
        // already set above by OOP-765.)
        const totalDuration = getChaptersTotalDuration(ctx.chapters);
        if (totalDuration > 0) {
          retVal.metadata.duration = totalDuration;
        }

        return retVal;
      }),
      // Exit rewind mode and go back to live
      // TODO: [CCS-986]: Potentially need to accommodate StandaloneChapter
      exitRewindMode: assign((ctx) => ({
        ...ctx,
        requestedOffset: undefined,
        episode: undefined,
        playLive: true,
        datumTimeProtected: false,
        lastSeekTimestamp: undefined,
        metadata: {
          ...ctx.metadata,
          datumTime: undefined,
        },
      })),
      sendReportEnded: send({
        type: 'REPORT_ENDED',
      }),
      playWithOffset: (ctx) => {
        const { player, episode, playLive } = ctx;
        console.log(`[MP3RewindStreamer] playWithOffset: ctx.source=${JSON.stringify(ctx.source)}, ctx.url=${ctx.url}`); // OOP-869 TODO: remove these console logs
        // Re-derive the same load target `loadAudio` resolved so we know
        // whether the player is holding an SSR/live URL (offset baked into
        // startTs) or a chapter MP3 (offset must be passed to the player).
        // deriveLoadAudioTarget is pure, so this mirrors loadAudio exactly.
        const target = deriveLoadAudioTarget(ctx);
        // TODO: fix this by expanding the machine states to differentiate between initial play and resume
        // This is to prevent sending reportPlaybackRequested to Conviva on a RESUME
        //
        // [OOP-756] `target.url` gates the WHOLE block, not just
        // setContentInfoConviva: latching lastPlayedContentId on a url-less
        // pass would make the eventual successful play of the same episode
        // skip both Conviva calls, leaving the session permanently without
        // content info. With no URL there is nothing to report yet — the
        // retry that produces a URL reports both.
        const currentContentId = episode?.getId();
        if (episode !== undefined && ctx.lastPlayedContentId !== currentContentId && target.url) {
          reportPlaybackRequestedConviva(player, episode, playLive, ctx.credentialsProvider?.userToken);
          ctx.lastPlayedContentId = currentContentId;
          setContentInfoConviva(player, playLive, target.url);
        }
        // [OOP-738] Unlike HLS (offset baked into the stream URL's startTs), each
        // chapter MP3 plays from 0, so start it at the intra-chapter seek offset
        // computed in loadAudio (getChapterSeekOffset — supersedes OOP-756's
        // inline within-chapter translation). playWithOffset(undefined) plays
        // from the current position (live / initial load).
        return ctx.player.playWithOffset(ctx.chapterSeekOffset);
      },
      saveResumePoint: assign((ctx): IMp3RewindStationMachineContext => saveMp3ResumePoints(ctx, true)),
      // This is geared towards making less API requests as a result of Audacy going down due to nationwide FEMA alert.
      cacheChaptersOnPause: (ctx) => {
        const id = ctx.episode?.getId() ?? '';
        void setQuickPauseChapterCache(id);
      },
      // Save the resume point every few seconds locally aka without API calls
      // With this approach we can save often and never lose our place.
      // using ts-ignore because, otherwise it throws error on using constant RESUME_POINT_INTERVAL_MSEC from other file
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      saveLocalResumePoint: assign((context): IMp3RewindStationMachineContext => {
        if (!context.timeSinceLastSavedResumePoint) {
          context.timeSinceLastSavedResumePoint = Date.now();
        }
        const currentTime = Date.now();
        const updateTime = context.timeSinceLastSavedResumePoint + RESUME_POINT_INTERVAL_MSEC;

        if (currentTime >= updateTime) {
          return saveMp3ResumePoints(context, false);
        }
        return context;
      }),
      // Reset the resume point to the beginning of the episode
      // Used when we finished an episode but don't want to resume from the end
      // Used to recover when GoLive playback fails as a workaround for a backend issue
      resetResumePoint: assign((ctx) => {
        const { elapsed, personalizationProvider, episode, standaloneChapter } = ctx;

        const entity = standaloneChapter ?? episode;

        if (!entity || !elapsed) {
          return ctx;
        }

        const resumePoint = entity.getStartTimeSeconds();

        void personalizationProvider.setPlayback(entity.getId(), resumePoint);

        ctx.elapsed = 0;
        return saveResumePoint(ctx, resumePoint);
      }),
      resetElapsed: assign((ctx) => {
        ctx.elapsed = 0;
        return ctx;
      }),
      // Live rollover: clear the ended episode + its chapters so the following
      // UPDATING_SCHEDULE re-resolves the current on-air show (getSchedule
      // Case 2, getEpisode(now)) and refetches chapters. Deliberately does NOT
      // stop the player — the live stream keeps playing across the boundary.
      // Also clear `metadata` (mirrors assignAdvancedShow) so the previous
      // show's fields that mergeMetadata does NOT refresh from the entity —
      // datumTime (elapsed anchor), isLive, contentType — don't linger and peg
      // the scrub bar / stale end time; datumTimeProtected is reset so the new
      // show's stream datumTime is accepted. The next assignMetadata rebuilds
      // start/stop/duration from the new entity.
      clearEpisodeForRollover: assign((ctx) => ({
        ...ctx,
        episode: undefined,
        // MUST clear: getSchedule Case 1 resolves the episode from
        // requestedOffset when there's no episode, so a stale offset (pointing
        // into the show that just ended) would re-resolve the OLD episode and
        // Case 2's getEpisode(now) would never run. Clearing it is what the
        // pause→play path (exitRewindMode) does, which is why that recovers.
        requestedOffset: undefined,
        chapters: undefined,
        currentChapterIndex: undefined,
        metadata: undefined,
        datumTimeProtected: false,
      })),
      // [OOP-729] Advance-trigger variant of resetElapsed: same EOF-guard
      // disarm, but keeps the end position for restoreElapsedAfterFailedAdvance.
      stashElapsedForAdvance: assign((ctx) => ({
        ...ctx,
        elapsedBeforeAdvance: ctx.elapsed,
        elapsed: 0,
      })),
      restoreElapsedAfterFailedAdvance: assign((ctx) => ({
        ...ctx,
        elapsed: ctx.elapsedBeforeAdvance ?? ctx.elapsed ?? 0,
        elapsedBeforeAdvance: undefined,
      })),
      // When we seek we need to update requestedOffset, elapsed, seekingInChapter etc.
      assignSeekTime: assign((ctx, { time }): IMp3RewindStationMachineContext => {
        const {
          episode,
          standaloneChapter,
          analyticsProvider,
          elapsed,
          metadata: { isLive = false } = {},
          deviceInfoProvider,
        } = ctx;

        const entity = standaloneChapter ?? episode;

        // [OOP-738] Clamp the seek target to this show's own timeline
        // [0, sum of chapter durations]. A backward skip near the start would
        // otherwise produce a negative show-relative time — and hence a
        // requestedOffset BEFORE chapter 0 — while a forward skip past the end
        // would overshoot the last chapter. Clamping pins both to the show's
        // own bounds. (The synthetic live chapter contributes 0 duration, so
        // seeks that should land on it are routed by the isSeekToLiveChapter
        // guard BEFORE this action runs.)
        const showDuration = getChaptersTotalDuration(ctx.chapters);
        time = showDuration > 0 ? Math.min(Math.max(0, time), showDuration) : Math.max(0, time);

        // [OOP-738] Convert to an absolute offset on the CHAPTER timeline
        // (anchor = chapter[0].startOffset). Deliberately not performSeek /
        // normalizeResumePoint: their episode-window validation treats
        // fixture-timeline offsets (OOP-794) as corrupted and resets the seek
        // to the episode start — the "every forward scrub restarts the chapter
        // from 0:00" bug.
        const requestedOffset = getOffsetFromSeekTime(ctx.chapters, time);
        // OOP-869 TODO: remove this diagnostic log
        console.log(
          `[MP3RewindStreamer] assignSeekTime: time=${time} (clamped, showDuration=${showDuration}), requestedOffset ${ctx.requestedOffset} -> ${requestedOffset}, elapsed ${ctx.elapsed} -> ${time}`,
        );
        const newCtx: IMp3RewindStationMachineContext = {
          ...ctx,
          seekingInChapter:
            ctx.currentChapterIndex !== undefined &&
            getChapterIndexFromOffset(ctx.chapters, requestedOffset) ===
              ctx.currentChapterIndex,
          requestedOffset,
          elapsed: time,
          metadata: {
            ...ctx.metadata,
            // ATL is StreamGuys, so keep datumTime undefined (same as performSeek)
            datumTime: ctx.metadata?.datumTime ? requestedOffset : undefined,
          },
        };

        const audioRoute = deviceInfoProvider?.getAudioRoute();

        if (elapsed !== undefined && entity?.data.id) {
          analyticsProvider?.sendPlayerEvent({
            type: time > elapsed ? PlayerAction.FAST_FORWARD : PlayerAction.REWIND,
            contentId: entity?.data.id as string,
            rewindFlag: isLive,
            currentPosition: toIntegerOrUndefined(entity.getStartTimeSeconds() + elapsed),
            streamUrl: getFirstAudioSourceUrl(entity?.data.streamUrl as string | undefined),
            connectionType: audioRoute,
          });
        }
        // performSeek only sets datumTime when metadata.datumTime was already present
        // (ATL/StreamGuys compat). MP3 streams never emit datumTime, so it stays
        // undefined on first play and mapPlayerTime falls back to entityStartTime,
        // computing elapsed=0 on the first REPORT_TIME_UPDATE after a seek. Force
        // datumTime=requestedOffset here so the scrubber starts at the right position.
        return {
          ...(newCtx),
          isLiveMode: false,
          // metadata.isLive must flip in the SAME action as isLiveMode: the
          // very next action in this transition (assignCurrentChapterIndexWithOffset)
          // calls resolveChapterIndex, which pins to the synthetic live chapter
          // whenever metadata.isLive is true — LOADING_SCHEDULE.onDone's
          // assignMetadata doesn't run until later, so a stale isLive here
          // pins currentChapterIndex to the URL-less chapter before this
          // seek's real target is ever resolved.
          metadata: { ...newCtx.metadata, isLive: false, datumTime: newCtx.requestedOffset },
        };
      }),
      // Every second we get a time update from AVPlayer/Exoplayer with `time` being
      // the elapsed seconds *within the current chapter's MP3 file* (it resets to 0
      // whenever loadAudio swaps in a new chapter). mapMp3PlayerTime adds the
      // cumulative duration of the previous chapters so `elapsed` is a continuous
      // show timeline and the scrub bar doesn't reset to 0 on chapter tap. [OOP-740]
      assignTime: assign((ctx, { time }): IMp3RewindStationMachineContext => {
        const mapped = mapMp3PlayerTime(ctx, time);
        // OOP-869 TODO: remove this diagnostic log — anomaly detector for
        // backward `elapsed` jumps during what should be continuous playback.
        //
        // DIAGNOSTIC ONLY — the flagged value is deliberately still persisted
        // below. Discarding/clamping a "backward" tick would be wrong:
        //   - After an in-chapter backward seek, a stale-high player tick can
        //     land before the native seek completes; the NEXT (correct, lower)
        //     tick is precisely the one this check flags. Holding the prior
        //     `elapsed` would freeze the scrubber on the stale value.
        //   - A genuinely anomalous tick self-corrects on the next
        //     REPORT_TIME_UPDATE: `elapsed` is recomputed fresh from
        //     getChapterTimelineStart(currentChapterIndex) + fileTime, and
        //     chapter resolution derives from `requestedOffset` (see
        //     resolveChapterIndex), never from `elapsed`.
        //   - Downstream consumers fail safe on a transiently low value: the
        //     resume re-anchor floors at 0 and end-of-content guards test
        //     `elapsed > total`, so a low tick can't fire them.
        if (ctx.elapsed !== undefined && mapped.elapsed < ctx.elapsed - 2) {
          console.warn(
            `[MP3RewindStreamer] ⚠️ elapsed jumped BACKWARD: ${ctx.elapsed} -> ${mapped.elapsed}` +
              ` (fileTime=${time}, currentChapterIndex=${ctx.currentChapterIndex},` +
              ` requestedOffset=${ctx.requestedOffset}, isLive=${ctx.metadata?.isLive},` +
              ` startOffsets=[${(ctx.chapters ?? []).map((c) => c.getStartOffset()).join(',')}])`,
          );
        }
        return { ...ctx, ...mapped };
      }),
      // For MP3 Rewind the loaded MP3 file *is* the current chapter, so the current
      // chapter is derived from requestedOffset (the same way loadAudio picks the
      // file) rather than from elapsed. Deriving from elapsed would let the index
      // drift mid-playback — fixture MP3 lengths don't line up with the real chapter
      // start offsets — which would shift the cumulative timeline base. When playing
      // live we still pin to the last (live) chapter. [OOP-740]
      assignCurrentChapterIndex: assign((ctx) => {
        const nextIndex = resolveChapterIndex(ctx);
        // OOP-869 TODO: remove this diagnostic log (auto-advance UI-reset
        // investigation) — logs only when the index actually CHANGES.
        if (nextIndex !== ctx.currentChapterIndex) {
          console.warn(
            `[MP3RewindStreamer] ⚠️ assignCurrentChapterIndex CHANGED: ${ctx.currentChapterIndex} -> ${nextIndex}` +
              ` (isLive=${ctx.metadata?.isLive}, requestedOffset=${ctx.requestedOffset}, elapsed=${ctx.elapsed},` +
              ` chapters=${ctx.chapters?.length}, startOffsets=[${(ctx.chapters ?? []).map((c) => c.getStartOffset()).join(',')}])`,
          );
        }
        return { currentChapterIndex: nextIndex };
      }),
      // Before loading audio, we need to set the correct chapter index
      assignCurrentChapterIndexWithOffset: assign((ctx) => {
        // [OOP-740] MP3 resolves the current chapter from `requestedOffset`, NOT
        // `elapsed`: this runs pre-load (LOADING_SCHEDULE.onDone / right after a
        // seek) when `elapsed` may still be 0/stale while `requestedOffset`
        // already points at the target chapter. OOP-765's elapsed-based
        // getCurrentChapterIndex would resolve the wrong chapter on deep-link /
        // resume here. FLAG for Vincent: confirm elapsed-based isn't required.
        return { ...ctx, currentChapterIndex: resolveChapterIndex(ctx) };
      }),
      // This allows us to override resume points and play live radio
      // We use this in CarPlay for a feature called "Jump back in live" as well as the "Go to live" button
      setPlayLive: assign((ctx: IMp3RewindStationMachineContext) => ({
        ...ctx,
        playLive: true,
      })),
      // We use this to clear the setPlayLive flag immediately after we've used it
      clearPlayLive: assign((ctx: IMp3RewindStationMachineContext) => ({
        ...ctx,
        playLive: false,
      })),
      // [OOP-730] Fired alongside the live-edge transition action triple in
      // PLAYING.IS_PLAYING.REPORT_TIME_UPDATE. Kept out of the guard so the
      // guard stays pure (no side effects per xstate convention).
      logLiveTransition: (ctx: IMp3RewindStationMachineContext) => {
        ctx.logger?.info('[OOP-730] caught up to live — transitioning to live edge', {
          episodeId: ctx.episode?.getId(),
          elapsed: ctx.elapsed,
          chapterCount: ctx.chapters?.length,
        });
      },
      // [OOP-737] Called from the explicit-seek branch of SEEK_TIME (in PLAYING,
      // PAUSED, and ENDED states). Once the user has scrubbed or tapped a chapter,
      // they've committed to rewind and subsequent PAUSE/RESUME should restore the
      // rewind offset rather than auto-returning to live. The end-of-content
      // SEEK_TIME branch intentionally does NOT call this, since hitting the end
      // of a live show's chapter is a natural return-to-live moment.

      // [OOP-737] Wired alongside `setPlayLive` on every path that lands the
      // listener at the live edge (GO_TO_LIVE, the wasPausedWhileLive auto-return on
      // PAUSED.PLAY, the isNonRewindableContent fall-through). See ctx field
      // doc-comment for why we can't rely on `metadata.isLive` from the player
      // when serving MP3 chunks at the live edge.
      setPlayingLiveEdge: assign((ctx: IMp3RewindStationMachineContext) => ({
        ...ctx,
        playingLiveEdge: true,
      })),
      // [OOP-737] Wired on explicit user seek (assignSeekTime clears isLiveMode).
      // Same semantic: the user has committed to rewind content, so we should
      // not be claiming "live" to the UI anymore.
      clearPlayingLiveEdge: assign((ctx: IMp3RewindStationMachineContext) => ({
        ...ctx,
        playingLiveEdge: false,
      })),
      // [OOP-738] MUST be wrapped in assign(): this was previously a plain
      // named function whose returned context was silently DISCARDED by xstate
      // — so REPORT_ENDED after the last chapter never actually set playLive /
      // cleared the stale episode+requestedOffset, and the machine reloaded the
      // old chapter (or, after a show rollover, resolved the stale offset to
      // the new episode's synthetic live chapter → NO_AUDIO_URL retry loop).
      handleRewindContentEnded: assign(
        (ctx: IMp3RewindStationMachineContext): IMp3RewindStationMachineContext => ({
          ...ctx,
          episode: undefined,
          playLive: true,
          // [OOP-738] BROADCAST always clears the offset: the vodFeatureEnabled
          // keep is an HLS-VOD resume semantic, but for MP3 rewind this action
          // means "hand off to live" — a kept offset from the ENDED episode
          // poisons the next load.
          requestedOffset:
            ctx.streamType !== 'BROADCAST' && ctx.player.vodFeatureEnabled
              ? ctx.requestedOffset
              : undefined,
        }),
      ),
      // Loads the current chapter's MP3 URL into the native player.
      //
      // [OOP-734] If the resolved chapter has no `audioUrl`, the action used
      // to silently skip `player.load(...)` and leave the machine in a
      // pseudo-loaded state with no actual playback — neither error nor
      // success surfaced to the listener. This now sends a synthetic
      // REPORT_FAILURE so the failure-with-retry path takes over and the
      // listener sees the same backoff/recovery behavior as any other load
      // failure. (Retries won't conjure a URL out of thin air, but they
      // exhaust cleanly and land in FAILURE — which is far better than a
      // silent stall.)
      // Loads the current chapter's MP3 URL — or the live stream — into the
      // native player. [OOP-765] deriveLoadAudioTarget makes the live-vs-chapter
      // decision (playLive / isLiveMode / seek-past-live) and returns the live
      // loadParams. [OOP-734] A missing url surfaces REPORT_FAILURE so the
      // retry/backoff path runs instead of stalling silently. [OOP-738] The
      // success branch grafts intra-chapter seek seeding (chapterSeekOffset +
      // show-relative elapsed) onto the chapter path.
      loadAudio: choose([
        {
          cond: (ctx: IMp3RewindStationMachineContext) => !deriveLoadAudioTarget(ctx).url,
          actions: [
            (ctx: IMp3RewindStationMachineContext) => {
              const { chapterIdx, requestedOffset } = deriveLoadAudioTarget(ctx);
              console.error(`[MP3RewindStreamer] loadAudio: chapter index=${chapterIdx}, url=MISSING (requestedOffset=${requestedOffset}) — sending REPORT_FAILURE`); // OOP-869 TODO: remove these console logs
            },
            assign((ctx: IMp3RewindStationMachineContext) => {
              const { chapterIdx } = deriveLoadAudioTarget(ctx);
              return { ...ctx, url: undefined, currentChapterIndex: chapterIdx, chapterSeekOffset: undefined };
            }),
            send({
              type: 'REPORT_FAILURE',
              errorMessage: 'Unable to load stream.',
              error: { type: 'NO_AUDIO_URL' },
            }),
          ],
        },
        {
          actions: assign((ctx: IMp3RewindStationMachineContext) => {
            const { chapterIdx, url, requestedOffset, usedLive, loadParams } = deriveLoadAudioTarget(ctx);
            ctx.player?.load({
              url: url as string,
              isPodcast: usedLive ? loadParams?.isPodcast ?? false : true,
              isAudioPreroll: false,
              liveContentUrl: '',
              autoplay: ctx.autoplay,
            });
            if (usedLive) {
              const { episode, item, personalizationProvider } = ctx;
              
              if (episode) {
                const episodeId = episode.getId();
                const itemId = item?.data?.id;
                const ids = typeof itemId === 'string' ? [episodeId, itemId] : [episodeId];
                
                personalizationProvider.getPlaybacks().then((stored) => {
                  const playbacks = ids
                    .filter((id) => stored[id] !== -1)
                    .map((id) => ({ contentId: id, playbackOffset: -1 }));
                  
                  if (playbacks.length > 0) {
                    personalizationProvider.setPlaybacks(playbacks, true);
                  }
                });
              }
              
              return {
                ...ctx,
                url,
                // Pin to the synthetic live chapter / end of the timeline.
                currentChapterIndex: ctx.chapters ? ctx.chapters.length - 1 : chapterIdx,
                elapsed: getChaptersTotalDuration(ctx.chapters),
                chapterSeekOffset: undefined,
                isLiveMode: true,
                metadata: { ...ctx.metadata, isLive: true },
              };
            }
            // [OOP-738] Chapter mode: seed the intra-chapter offset and matching
            // show-relative elapsed so the scrub bar lands on the exact seek
            // point and playWithOffset starts the file there. metadata.isLive is
            // cleared here too so switching from live to a chapter drops the
            // stale live indicator immediately (plain MP3s emit no metadata event).
            const chapterSeekOffset = getChapterSeekOffset(ctx.chapters, chapterIdx, requestedOffset);
            const elapsed = getChapterTimelineStart(ctx.chapters, chapterIdx) + chapterSeekOffset;
            console.log(`[MP3RewindStreamer] loadAudio: chapter index=${chapterIdx}, url=${url} (requestedOffset=${requestedOffset}, seekOffset=${chapterSeekOffset})`); // OOP-869 TODO: remove these console logs
            return {
              ...ctx,
              url,
              currentChapterIndex: chapterIdx,
              elapsed,
              chapterSeekOffset,
              isLiveMode: false,
              metadata: { ...ctx.metadata, isLive: false },
            };
          }),
        },
      ]),
      setIsLiveAndRequestedOffset: assign((ctx, e) => {
        // Stale-vs-adoptable decision shared with the
        // isContinuePlayingStaleToLive guard — see isStaleContinueOffsetToLive
        // for the rationale. [OOP-765]
        if (isStaleContinueOffsetToLive(ctx, e)) {
          ctx.logger?.info(
            `[OOP-765] setIsLiveAndRequestedOffset: playlistNextStartTime ${e.playlistNextStartTime} outside episode window — routing to live`,
          );
          return {
            ...ctx,
            playLive: true,
            seekingInChapter: false,
            requestedOffset: undefined,
          };
        }

        return {
          ...ctx,
          playLive: e.playLive,
          seekingInChapter: false,
          requestedOffset: e.playlistNextStartTime,
        };
      }),
      // [OOP-738] In-chapter scrub: the target chapter's MP3 is already loaded, so
      // seek within the file (no reload). assignSeekTime has already updated
      // requestedOffset and elapsed; this just moves the native playhead. Mirrors
      // PodcastEpisodeMachine.seekTime.
      seekWithinChapter: (ctx: IMp3RewindStationMachineContext) => {
        const { player, chapters, currentChapterIndex, requestedOffset } = ctx;
        const offset = getChapterSeekOffset(chapters, currentChapterIndex ?? 0, requestedOffset);
        // OOP-869 TODO: remove this diagnostic log
        console.log(
          `[MP3RewindStreamer] seekWithinChapter: setPosition(${offset}) (chapterIdx=${currentChapterIndex}, requestedOffset=${requestedOffset})`,
        );
        player?.setPosition(offset);
      },
      // [OOP-738] Natural end of a non-last chapter → advance to the next chapter
      // and point the load at its start so LOADING → loadAudio plays the next MP3
      // from 0. Only invoked when hasNextChapter is true (a next real chapter
      // exists), so it never steps onto the synthetic live chapter / OOP-730 path.
      advanceToNextChapter: assign((ctx: IMp3RewindStationMachineContext) => {
        const { chapters, currentChapterIndex } = ctx;
        const nextIdx = (currentChapterIndex ?? 0) + 1;
        const nextChapter = chapters?.[nextIdx];
        if (!nextChapter) {
          return ctx;
        }
        // OOP-869 TODO: remove this diagnostic log
        console.log(
          `[MP3RewindStreamer] advanceToNextChapter: ${currentChapterIndex} -> ${nextIdx} (requestedOffset -> ${nextChapter.getStartOffset()})`,
        );
        return {
          ...ctx,
          currentChapterIndex: nextIdx,
          requestedOffset: nextChapter.getStartOffset(),
          chapterSeekOffset: 0,
        };
      }),
      // Analytics stop event
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      sendStopEvent: async ({
        analyticsProvider,
        episode,
        firstLoadTime,
        retryCount,
        errors,
        elapsed,
        contentType,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }

        const audioRoute = deviceInfoProvider?.getAudioRoute();

        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.STOP,
          contentId: contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });

        const { bandwidthEstimateBPS, historicBandwidthEstimateBPS, type } =
          (await deviceInfoProvider?.getNetworkInfo()) || {};

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_PAUSE,
          eventDetails: {
            isLive,
            stationId: contentId,
            stationName: episode ? episode.getTitle() : undefined,
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            bandwidthEstimateBPS,
            historicBandwidthEstimateBPS,
            connectivityType: type,
            isNativeErrorHandling: true,
          },
        });
        if (retryCount) {
          analyticsProvider.sendEventToListener({
            type: PlayerMetric.STREAM_RECONNECT_ABORTED,
            eventDetails: {
              isLive,
              stationId: contentId,
              stationName: episode ? episode.getTitle() : undefined,
              streamerType: contentType,
              uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
              retryCount,
              mostRecentError: errors?.[0],
              bandwidthEstimateBPS,
              historicBandwidthEstimateBPS,
              connectivityType: type,
              isNativeErrorHandling: true,
            },
          });
        }
      },
      // [OOP-731] Analytics for OOP-730's auto-jump-to-live trigger.
      // Manual GO_TO_LIVE button taps fire `sendJumpToLive(contentId)` from
      // the UI layer (useAudioHelpers.ts#seekToLivePosition) — that path
      // doesn't reach the state machine. This action covers the auto-jump
      // case (last-chapter completion, no UI input), and uses a distinct
      // PlayerAction enum value so downstream analytics can separate
      // user-initiated vs system-initiated live-jumps.
      //
      // Siblings for the other MP3-Rewind-only transitions:
      //   - `sendLiveOriginResumeEvent` — OOP-737 pause-resume → live edge
      //   - `sendAutoAdvanceShowEvent` — OOP-729 auto-advance to next show
      //   - `sendSchedulePollFailedEvent` — OOP-734 UPDATING_SCHEDULE
      //     recovery (low-severity observability)
      // Chapter-retry exhaustion (also called out in OOP-731's AC) is
      // wired via `sendChaptersRefreshFailedEvent` at CHAPTERS_REFRESH_FAILED
      // entry — see OOP-734's implementation.
      sendAutoJumpToLiveEvent: ({
        analyticsProvider,
        episode,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }
        const audioRoute = deviceInfoProvider?.getAudioRoute();
        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.AUTO_JUMP_TO_LIVE,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });
      },
      // [OOP-731] Analytics for OOP-737's live-origin pause-resume return.
      // Fires when the `wasPausedWhileLive`-guarded branch on PAUSED.PLAY routes
      // the listener back to the live edge because they were listening live
      // before entering Rewind. Payload shape mirrors `sendAutoJumpToLiveEvent`
      // so the two auto-live-return paths are directly comparable in the
      // analytics pipeline; the `type` field is what distinguishes them.
      sendLiveOriginResumeEvent: ({
        analyticsProvider,
        episode,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }
        const audioRoute = deviceInfoProvider?.getAudioRoute();
        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.LIVE_ORIGIN_RESUME,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });
      },
      // [OOP-731] Analytics for OOP-729's auto-advance-to-next-show
      // transition. Fires on onDone of the `advanceToNextShow` service —
      // the point at which `assignAdvancedShow` has updated `ctx.episode`
      // to the incoming show, so `contentId` in the payload is the NEW
      // show's id. The outgoing show has already fired its own
      // `sendStopEvent` from the REPORT_TIME_UPDATE branch that entered
      // ADVANCING_TO_NEXT_SHOW, so the pair (stop of old show →
      // AUTO_ADVANCE_SHOW of new show) fully brackets the transition in
      // the analytics stream.
      sendAutoAdvanceShowEvent: ({
        analyticsProvider,
        episode,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }
        const audioRoute = deviceInfoProvider?.getAudioRoute();
        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.AUTO_ADVANCE_SHOW,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });
      },
      // [OOP-731] Low-severity observability signal for OOP-734's
      // UPDATING_SCHEDULE.onError default branch. Fires once per failed
      // schedule poll so the analytics pipeline can flag transient backend
      // issues without correlating through log ingestion. The next
      // SCHEDULE_UPDATE_INTERVAL poll re-arms independently; if it
      // succeeds, no explicit "recovered" event fires — recovery is
      // implicit in the absence of subsequent failure events.
      // [OOP-731] User-initiated seek that resolves to the live edge — wired on
      // OOP-738's isSeekToLiveChapter and OOP-765's hasSeekedPastLive SEEK_TIME
      // branches (PLAYING / PAUSED / ENDED). Payload matches
      // sendAutoJumpToLiveEvent for pipeline comparability; `type`
      // distinguishes manual scrub-to-live from auto catch-up-to-live.
      sendMp3SeekToLiveEvent: ({
        analyticsProvider,
        episode,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }
        const audioRoute = deviceInfoProvider?.getAudioRoute();
        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.MP3_SEEK_TO_LIVE,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });
      },
      // [OOP-731] Auto-advance from one MP3 chapter to the next within the same
      // show — wired on OOP-738's hasNextChapter (REPORT_ENDED) and
      // isFinishedNonLastChapter (REPORT_PAUSED, the PAUSE-at-chapter-EOF
      // native quirk). MUST run AFTER advanceToNextChapter. Distinct from
      // AUTO_ADVANCE_SHOW (episode→episode).
      sendAutoAdvanceChapterEvent: ({
        analyticsProvider,
        episode,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!contentId) {
          return;
        }
        const audioRoute = deviceInfoProvider?.getAudioRoute();
        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.AUTO_ADVANCE_CHAPTER,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data?.streamUrl as string | undefined),
          connectionType: audioRoute,
        });
      },
      // [OOP-731] Observability for OOP-729's auto-advance failure fallback
      // (no next show in schedule / network failure → PAUSED).
      sendAutoAdvanceFailedEvent: ({
        analyticsProvider,
        contentType,
        item,
        firstLoadTime,
        metadata: { isLive = false } = {},
      }: IMp3RewindStationMachineContext) => {
        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_AUTO_ADVANCE_FAILED,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
          },
        });
      },
      sendSchedulePollFailedEvent: ({
        analyticsProvider,
        contentType,
        item,
        firstLoadTime,
        retryCount,
        metadata: { isLive = false } = {},
      }: IMp3RewindStationMachineContext) => {
        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_SCHEDULE_POLL_FAILED,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount: retryCount ?? 0,
          },
        });
      },
      // Analytics play event
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      sendPlayEvent: async ({
        analyticsProvider,
        episode,
        firstLoadTime,
        retryCount,
        contentType,
        elapsed,
        metadata: { isLive = false } = {},
        deviceInfoProvider,
        errors,
      }) => {
        const contentId = episode?.data?.id as string | undefined;
        if (!episode || !contentId) {
          return;
        }

        const audioRoute = deviceInfoProvider?.getAudioRoute();

        analyticsProvider.sendPlayerEvent({
          type: PlayerAction.PLAY,
          contentId,
          rewindFlag: isLive,
          currentPosition: episode
            ? toIntegerOrUndefined(episode.getStartTimeSeconds() + (elapsed ?? 0))
            : undefined,
          streamUrl: getFirstAudioSourceUrl(episode?.data.streamUrl),
          connectionType: audioRoute,
        });

        const { bandwidthEstimateBPS, historicBandwidthEstimateBPS, type } =
          (await deviceInfoProvider?.getNetworkInfo()) || {};

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_PLAY,
          eventDetails: {
            isLive,
            stationId: contentId,
            stationName: episode ? episode.getTitle() : undefined,
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            bandwidthEstimateBPS,
            historicBandwidthEstimateBPS,
            connectivityType: type,
            isNativeErrorHandling: true,
          },
        });
        if (retryCount) {
          analyticsProvider.sendEventToListener({
            type: PlayerMetric.STREAM_RECONNECT_SUCCESSFUL,
            eventDetails: {
              isLive,
              stationId: contentId,
              stationName: episode ? episode.getTitle() : undefined,
              streamerType: contentType,
              uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
              retryCount,
              mostRecentError: errors?.[0],
              bandwidthEstimateBPS,
              historicBandwidthEstimateBPS,
              connectivityType: type,
              isNativeErrorHandling: true,
            },
          });
        }
      },
      // [OOP-734] Emitted on entry to CHAPTERS_REFRESH_FAILED. Audio is
      // still playing; this just lets the analytics pipeline know the
      // chapter list went stale (and gives QA / observability a hook for
      // the new error-state surface).
      sendChaptersRefreshFailedEvent: ({
        analyticsProvider,
        contentType,
        item,
        firstLoadTime,
        chapterRetryCount,
        metadata: { isLive = false } = {},
      }: IMp3RewindStationMachineContext) => {
        console.error(`[MP3RewindStreamer] CHAPTERS_REFRESH_FAILED entered — chapterRetryCount=${chapterRetryCount}`); // OOP-869 TODO: remove these console logs
        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_CHAPTERS_REFRESH_FAILED,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount: chapterRetryCount ?? 0,
          },
        });
      },
      sendBufferingEvent: assign(() => {
        bufferingEventCounter.addEvent('end_buffering');
        // analyticsProvider.sendEventToListener({
        //   type: PlayerMetric.STREAM_BUFFERING,
        //   eventDetails: {
        //     isLive,
        //     stationId: item.getId(),
        //     streamerType: contentType,
        //     uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
        //     isNativeErrorHandling: true,
        //   },
        // });
        return {
          bufferStartTime: Date.now(),
        };
      }),
      sendResumeFromBufferingEvent: assign(() => {
        bufferingEventCounter.addEvent('end_buffering');
        //   analyticsProvider.sendEventToListener({
        //   type: PlayerMetric.STREAM_RESUME_FROM_BUFFERING,
        //     eventDetails: {
        //     isLive,
        //     stationId: item.getId(),
        //     streamerType: contentType,
        //     uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
        //     timeBufferingMs: bufferStartTime ? Date.now() - bufferStartTime : 0,
        //     isNativeErrorHandling: true,
        //   },
        // });
        return { bufferStartTime: undefined };
      }),
      sendNativePlaybackFailedEvent: (
        {
          analyticsProvider,
          contentType,
          item,
          firstLoadTime,
          retryCount,
          metadata: { isLive = false } = {},
        },
        event,
      ) => {
        console.error('[MP3RewindStreamer] REPORT_PLAYBACK_FAILED received → entering FAILURE_WITH_RETRY. Event:', JSON.stringify(event, null, 2)); // OOP-869 TODO: remove these console logs
        AudacyLogger.info(
          `[${
            LoggerTopic.Streaming
          }]: RewindStationMachine: sendNativePlaybackFailedEvent event:\n${JSON.stringify(
            event,
            null,
            2,
          )}`,
        );

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_PLAYBACK_FAILED,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            error: {
              errorMessage: event?.errorMessage,
              errorType: event?.error,
            },
            isNativeErrorHandling: true,
          },
        });
      },
      sendPlaybackFailedToLoadEvent: (
        {
          analyticsProvider,
          contentType,
          item,
          firstLoadTime,
          retryCount,
          metadata: { isLive = false } = {},
        },
        event,
      ) => {
        console.error('[MP3RewindStreamer] loadSources/getSchedule FAILED → entering FAILED_TO_LOAD → retry. Error event:', JSON.stringify(event, null, 2)); // OOP-869 TODO: remove these console logs
        AudacyLogger.info(
          `[${
            LoggerTopic.Streaming
          }]: RewindStationMachine: sendPlaybackFailedToLoadEvent event:\n${JSON.stringify(
            event,
            null,
            2,
          )}`,
        );

        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_FAILED_TO_LOAD,
          eventDetails: {
            isLive,
            stationId: item.getId(),
            stationName: item.getTitle(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            retryCount,
            error: {
              ...event,
            },
            isNativeErrorHandling: true,
          },
        });
      },
      ...DEFAULT_PLAYER_ACTIONS,
      // Override autoplay to add LOADED state logging (logs run before autoplay sends PLAY to parent)
      autoplay: choose([
        {
          cond: ({ autoplay }: IMp3RewindStationMachineContext) => {
            console.log(`[MP3RewindStreamer] LOADED state entered — autoplay=${autoplay}`); // OOP-869 TODO: remove these console logs
            return autoplay ?? false;
          },
          actions: [
            sendParent((ctx: IMp3RewindStationMachineContext) => ({
              type: 'PLAY',
              autoplay: ctx.autoplay,
            })),
          ],
        },
        {
          actions: [
            sendParent({ type: 'PAUSE' }),
            assign(() => ({ autoplay: true })),
          ],
        },
      ]),
      // Override logRetry to add full context
      logRetry: ({
        item,
        retryCount = 0,
        errors,
        firstLoadTime,
        analyticsProvider,
        contentType,
      }: IMp3RewindStationMachineContext) => {
        // OOP-869 TODO: remove these console logs
        console.warn(
          `[MP3RewindStreamer] RETRY #${retryCount} for item=${item.getId()} contentType=${contentType} errors=${JSON.stringify(errors)}`,
        );
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          AudacyLogger.log(
            `[StationMachine] StreamerUtils: Retry Attempt ${retryCount} for ${item.getId()}`,
          );
        }
        analyticsProvider.sendEventToListener({
          type: PlayerMetric.STREAM_RECONNECT_ATTEMPT,
          eventDetails: {
            stationId: item.getId(),
            streamerType: contentType,
            uptimeMs: firstLoadTime ? Date.now() - firstLoadTime : 0,
            mostRecentError: errors?.[0],
            retryCount,
          },
        });
      },
    },
  },
);

const selector: TStreamSelector<typeof mp3RewindStationMachine> = async (
  dataObject,
  _dataProvider,
  logger,
) => {
  // Scheduled episodes can contain HLS-only chapters even when the MP3
  // streamer feature flag is enabled. In that case, let the next selector use
  // the existing HLS rewind machine.
  if (dataObject instanceof Episode) {
    let chapters: Chapter[] = [];
    try {
      chapters = (await dataObject.getChapters()) ?? [];
    } catch {
      return;
    }
    if (!hasFullMp3ChapterCoverage(chapters)) {
      return;
    }
  }

  const playableDataObject = await getPlayableDataObject(dataObject);

  if (!playableDataObject) {
    return;
  }

  logger?.info('Using server-side rewind provider', dataObject);
  console.log('[MP3RewindStreamer] Streamer selected for', dataObject); // OOP-869 TODO: remove these console logs

  const { station, episode, streamType, standaloneChapter } = playableDataObject;

  return mp3RewindStationMachine.withContext({
    ...mp3RewindStationMachine.context,
    item: streamType === 'BROADCAST' ? station : dataObject, // the UI expects ctx.item to be the station not the episode in this case
    streamType,
    station,
    episode,
    standaloneChapter,
  });
};

// Exported for unit tests so that guards, actions, and after-branch
// conds can be inspected via `mp3RewindStationMachine.options.*` /
// `.states.*.config.*` without needing to spin up the full interpret()
// pipeline.
export { mp3RewindStationMachine };

export default selector;
